import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import MapToolbar from './MapToolbar';
import MapGrid from './MapGrid';
import MapZone from './MapZone';
import MapAisle from './MapAisle';
import MapPlatform from './MapPlatform';
import MapRack from './MapRack';
import MapEntryExit from './MapEntryExit';
import MapObstacle from './MapObstacle';
import MapPropertiesPanel from './MapPropertiesPanel';
import MapSortOrderOverlay from './MapSortOrderOverlay';
import MapRoutePreview from './MapRoutePreview';
import {
  snapToGrid,
  generateTempId,
  ENTRY_EXIT_GRID_SIZE,
  entryCenterX,
  entryCenterY,
  ZONE_COLORS,
  ZONE_BORDER_COLORS,
  calculateRouteDistance,
  clampPositionToBounds,
  clampSizeToBounds,
  rectFromLocation,
  rectsOverlap,
  findContainingZone,
  findParentLocation,
  isFullyInsideBounds,
  determinePlatformSide,
  snapPlatformToAisleSide,
  platformBoundsForAisle,
  platformBoundsInsideAisle,
  findContainingAisle,
  isPlatformInsideAisle,
  getAisleWalkableCells,
  wouldCollide,
  aStarPath,
  isVerticallyStackedRack,
  countChildRacks,
} from './mapTypes';
import type { MapTool, LocationMapItem, EntryExitItem, ObstacleItem, MapSelectedItem, MapLayerVisibility, RoutePoint, BoundsRect, AisleSide } from './mapTypes';
import { locationService } from '../../services/locationService';
import { smartWarehouseService } from '../../services/smartWarehouseService';
import { InventoryService } from '../../../accounting/inventory/services/inventoryService';
import { useTranslation } from 'react-i18next';
import { buildDefaultLocationName } from '../../shared/locationLabels';

interface Warehouse2DMapEditorProps {
  warehouseId: string;
  onDataChanged: () => void;
  showSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

interface UndoEntry {
  locations: LocationMapItem[];
  entryExits: EntryExitItem[];
  obstacles: ObstacleItem[];
  warehouseWidth: number;
  warehouseHeight: number;
}

interface StockByLocationRow {
  id: string;
  productId: string;
  productName: string;
  productBarcode: string;
  quantity: number;
  locationId: string | null;
  locationCode: string;
  locationName: string;
}

interface RouteStepTarget {
  kind: 'stop' | 'exit';
  endIndex: number;
  label: string;
  gridX: number;
  gridY: number;
  locationId?: string;
  exitId?: string;
}

const OBSTACLE_STORAGE_KEY = 'wms_map_obstacles_';
const MAP_SIZE_STORAGE_KEY = 'wms_map_size_';
const ROUTE_RACK_CODES_STORAGE_KEY = 'wms_map_route_rack_codes_';
const ROUTE_STEP_DEBUG = true;

// Depo grid boyutu için localStorage anahtarı (multi-tenant: warehouseId zaten unique).
// Migration yasak olduğu için DB sütunu ekleyemiyoruz; warehouse'a ait ayarı tarayıcıda saklıyoruz.
function readStoredMapSize(warehouseId: string): { w?: number; h?: number } {
  try {
    const raw = localStorage.getItem(MAP_SIZE_STORAGE_KEY + warehouseId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const w = typeof parsed.w === 'number' && parsed.w > 0 ? parsed.w : undefined;
    const h = typeof parsed.h === 'number' && parsed.h > 0 ? parsed.h : undefined;
    return { w, h };
  } catch {
    return {};
  }
}

function writeStoredMapSize(warehouseId: string, w: number, h: number) {
  try {
    localStorage.setItem(MAP_SIZE_STORAGE_KEY + warehouseId, JSON.stringify({ w, h }));
  } catch {
    // localStorage quota / private mode → sessizce yut.
  }
}

// --- helpers ---

function flattenTree(
  nodes: any[],
  parentId: string | null = null,
  visited: Set<string> = new Set(),
): LocationMapItem[] {
  const items: LocationMapItem[] = [];
  for (const n of nodes) {
    const id: string | undefined = n.Id || n.id;
    // Cycle koruması — hatalı backend payload'unda aynı node tekrar görünürse sonsuz döngüye düşmeyelim.
    if (id && visited.has(id)) continue;
    if (id) visited.add(id);

    const type = n.Type || n.type || 'Zone';
    if (['Zone', 'Aisle', 'Platform', 'Rack'].includes(type)) {
      items.push({
        id: id || '',
        type: type as LocationMapItem['type'],
        code: n.Code || n.code || '',
        name: n.Name || n.name || '',
        gridX: n.GridX ?? n.gridX ?? 0,
        gridY: n.GridY ?? n.gridY ?? 0,
        gridWidth: n.GridWidth ?? n.gridWidth ?? (type === 'Rack' ? 1 : type === 'Zone' ? 8 : type === 'Aisle' ? 2 : 3),
        gridHeight: n.GridHeight ?? n.gridHeight ?? (type === 'Rack' ? 1 : type === 'Zone' ? 6 : 10),
        parentId: n.ParentId ?? n.parentId ?? parentId,
        aisleId: n.AisleId ?? n.aisleId ?? null,
        aisleSide: n.AisleSide ?? n.aisleSide ?? null,
        sortOrder: n.SortOrder ?? n.sortOrder ?? 0,
        isPickable: n.IsPickable ?? n.isPickable ?? (type === 'Rack'),
        isNew: false,
        isModified: false,
        isDeleted: false,
      });
    }
    const children = n.Children || n.children || [];
    if (children.length > 0) {
      items.push(...flattenTree(children, id ?? parentId, visited));
    }
  }
  return items;
}

function mapGraphNodes(nodes: any[]): EntryExitItem[] {
  return nodes.filter((n: any) => {
    const nt = n.NodeType || n.nodeType || '';
    return nt === 'Entry' || nt === 'Exit';
  }).map((n: any) => ({
    id: n.Id || n.id,
    nodeType: (n.NodeType || n.nodeType) as 'Entry' | 'Exit',
    name: n.Name || n.name || '',
    gridX: n.GridX ?? n.gridX ?? 0,
    gridY: n.GridY ?? n.gridY ?? 0,
    isNew: false,
    isModified: false,
    isDeleted: false,
  }));
}

/** Bir metnin içindeki ilk sayı dizisini çıkarır: "A2" -> 2, "Koridor 12" -> 12, "P-021" -> 21. */
function extractLeadingNumber(s?: string | null): number | null {
  if (!s) return null;
  const m = s.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

/**
 * Koridorun numarasını döndürür (peron numaralandırması bu numarayı temel alır).
 * Öncelik: koridor kodu (A2) → koridor adı (Koridor 2) → koridorun çizim sırası (index+1).
 */
function getAisleNumber(aisle: LocationMapItem, locations: LocationMapItem[]): number {
  const fromCode = extractLeadingNumber(aisle.code);
  if (fromCode != null) return fromCode;
  const fromName = extractLeadingNumber(aisle.name);
  if (fromName != null) return fromName;
  const aisleList = locations.filter(l => l.type === 'Aisle' && !l.isDeleted);
  const idx = aisleList.findIndex(l => l.id === aisle.id);
  return idx >= 0 ? idx + 1 : aisleList.length + 1;
}

// --- Component ---

const Warehouse2DMapEditor: React.FC<Warehouse2DMapEditorProps> = ({
  warehouseId, onDataChanged, showSnackbar,
}) => {
  const { t } = useTranslation('warehouse');
  const [tool, setTool] = useState<MapTool>('select');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize] = useState(20);
  const [warehouseWidth, setWarehouseWidth] = useState(40);
  const [warehouseHeight, setWarehouseHeight] = useState(30);
  const [locations, setLocations] = useState<LocationMapItem[]>([]);
  const [entryExits, setEntryExits] = useState<EntryExitItem[]>([]);
  const [obstacles, setObstacles] = useState<ObstacleItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedKind, setSelectedKind] = useState<'location' | 'entryExit' | 'obstacle'>('location');
  const [saving, setSaving] = useState(false);
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);

  // Drawing state
  const drawStart = useRef<{ gridX: number; gridY: number } | null>(null);
  const [drawRect, setDrawRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  // Drag state
  const dragInfo = useRef<{
    id: string;
    kind: string;
    origX: number;
    origY: number;
    startClientX: number;
    startClientY: number;
    linkedLocationIds?: string[];
    linkedOriginPositions?: Record<string, { x: number; y: number }>;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Resize state
  const resizeInfo = useRef<{ id: string; kind: string; origW: number; origH: number; startClientX: number; startClientY: number } | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Bulk rack dialog
  const [bulkRackTarget, setBulkRackTarget] = useState<LocationMapItem | null>(null);
  const [bulkRackCount, setBulkRackCount] = useState(4);
  const [bulkShelfLevels, setBulkShelfLevels] = useState(5);

  const [warehouseOccupancy, setWarehouseOccupancy] = useState<{
    occupancyRatePercent: number;
    occupiedLocations: number;
    totalPickableLocations: number;
    emptyLocations: number;
  } | null>(null);

  const loadWarehouseOccupancy = useCallback(async () => {
    try {
      const detail = await smartWarehouseService.getByIdDetail(warehouseId);
      if (detail && detail.totalPickableLocations != null) {
        setWarehouseOccupancy({
          occupancyRatePercent: detail.occupancyRatePercent ?? 0,
          occupiedLocations: detail.occupiedLocations ?? 0,
          totalPickableLocations: detail.totalPickableLocations ?? 0,
          emptyLocations: detail.emptyLocations ?? 0,
        });
      } else {
        setWarehouseOccupancy(null);
      }
    } catch {
      setWarehouseOccupancy(null);
    }
  }, [warehouseId]);

  useEffect(() => {
    loadWarehouseOccupancy();
  }, [loadWarehouseOccupancy]);

  // H3: Layers, SortOrder, Route
  const [layers, setLayers] = useState<MapLayerVisibility>({ sortOrder: false, route: false, obstacles: true, labels: true });
  const [sortOrderLoading, setSortOrderLoading] = useState(false);
  const [routePoints, setRoutePoints] = useState<RoutePoint[]>([]);
  const [fullRoutePoints, setFullRoutePoints] = useState<RoutePoint[]>([]);
  const [routeDistance, setRouteDistance] = useState(0);
  const [routeStepIndex, setRouteStepIndex] = useState(-1);
  const [routeStrategy, setRouteStrategy] = useState<'optimal' | 'leftFirst' | 'rightFirst'>('optimal');
  const [isOrderRackSelectionActive, setIsOrderRackSelectionActive] = useState(false);
  const [routeRackCodes, setRouteRackCodes] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ROUTE_RACK_CODES_STORAGE_KEY + warehouseId);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((x) => String(x || '').trim().toUpperCase())
        .filter((x) => x.length > 0);
    } catch {
      return [];
    }
  });
  const [occupiedLocationIds, setOccupiedLocationIds] = useState<Set<string>>(new Set());
  const [rackStockRows, setRackStockRows] = useState<StockByLocationRow[]>([]);
  const [rackStockLoading, setRackStockLoading] = useState(false);
  const [rackInfoOpen, setRackInfoOpen] = useState(false);

  // Yan panel açılıp kapanabilir (büyük depolarda haritaya yer açmak için)
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRootRef = useRef<HTMLDivElement>(null);
  const mapViewportRef = useRef<HTMLDivElement>(null);
  const didInitialFitRef = useRef(false);
  const mapSizeHydratedRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(ROUTE_RACK_CODES_STORAGE_KEY + warehouseId, JSON.stringify(routeRackCodes));
    } catch {
      // ignore localStorage failures
    }
  }, [warehouseId, routeRackCodes]);

  const cellPx = gridSize * zoom;
  // Öğeler SABİT temel hücre boyutuyla (zoom'dan bağımsız) çizilir; zoom/pan ise MapGrid içinde
  // CSS transform (GPU) ile uygulanır. Böylece zoom/pan sırasında binlerce raf yeniden RENDER
  // EDİLMEZ → "aşırı kasma" ortadan kalkar. Sürükleme matematiği gerçek cellPx'i kullanmaya devam eder.
  const baseCell = gridSize;
  const selectedRack = useMemo(
    () => (selectedKind === 'location'
      ? locations.find((l) => l.id === selectedId && l.type === 'Rack' && !l.isDeleted) || null
      : null),
    [locations, selectedId, selectedKind]
  );

  const loadOccupiedLocations = useCallback(async () => {
    if (!warehouseId) return;
    try {
      const resp = await InventoryService.getStockByLocation(warehouseId, { page: 1, pageSize: 10000 });
      const items = (resp?.data ?? []) as any[];
      const occupied = new Set(
        items
          .map((row: any) => row.locationId || row.LocationId)
          .filter((id: string | null | undefined): id is string => !!id)
      );
      setOccupiedLocationIds(occupied);
    } catch (err) {
      console.error('[WMS MAP] Occupied locations load error:', err);
      setOccupiedLocationIds(new Set());
    }
  }, [warehouseId]);

  useEffect(() => {
    // Warehouse değiştiğinde önce load etkisinin (localStorage + item max) state'i kurmasına izin ver.
    mapSizeHydratedRef.current = false;
  }, [warehouseId]);

  useEffect(() => {
    loadOccupiedLocations();
  }, [loadOccupiedLocations]);

  useEffect(() => {
    const loadRackStocks = async () => {
      if (!warehouseId || !selectedRack?.id) {
        setRackStockRows([]);
        return;
      }

      try {
        setRackStockLoading(true);
        const resp = await InventoryService.getStockByLocation(warehouseId, {
          locationId: selectedRack.id,
          page: 1,
          pageSize: 10000,
        });
        const rows = (resp?.data ?? []).map((row: any) => ({
          id: row.id || row.Id,
          productId: row.productId || row.ProductId,
          productName: row.productName || row.ProductName || '-',
          productBarcode: row.productBarcode || row.ProductBarcode || '-',
          quantity: Number(row.quantity ?? row.Quantity ?? 0),
          locationId: row.locationId || row.LocationId || null,
          locationCode: row.locationCode || row.LocationCode || '-',
          locationName: row.locationName || row.LocationName || '-',
        })) as StockByLocationRow[];
        setRackStockRows(rows);
      } catch (err) {
        console.error('[WMS MAP] Rack stock load error:', err);
        setRackStockRows([]);
      } finally {
        setRackStockLoading(false);
      }
    };

    loadRackStocks();
  }, [warehouseId, selectedRack?.id]);

  const handleRackSelect = useCallback((id: string) => {
    const rack = locations.find((l) => l.id === id && l.type === 'Rack' && !l.isDeleted);
    if (!rack) return;
    if (isOrderRackSelectionActive) {
      const code = (rack.code || '').trim().toUpperCase();
      if (!code) {
        showSnackbar('Bu rafın kodu bulunamadı.', 'error');
        return;
      }
      setRouteRackCodes((prev) => (
        prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]
      ));
      return;
    }
    setSelectedKind('location');
    setSelectedId(id);
    setRackInfoOpen(true);
  }, [locations, isOrderRackSelectionActive, showSnackbar]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === fullscreenRootRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Depo boyutu localStorage'a senkron yazılır (warehouseId scope'lu, multi-tenant uyumlu).
  // Undo sonrası state geri dönerse buraya da yansır → kalıcı durum tutarlı kalır.
  useEffect(() => {
    if (!warehouseId || !mapSizeHydratedRef.current) return;
    writeStoredMapSize(warehouseId, warehouseWidth, warehouseHeight);
  }, [warehouseId, warehouseWidth, warehouseHeight]);

  // Haritayı viewport'a sığdırıp ortala
  const fitToScreen = useCallback(() => {
    const vp = mapViewportRef.current;
    if (!vp) return;
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    if (vw <= 0 || vh <= 0) return;

    const padding = 32;
    const usableW = Math.max(1, vw - padding * 2);
    const usableH = Math.max(1, vh - padding * 2);
    const baseW = Math.max(1, warehouseWidth * gridSize);
    const baseH = Math.max(1, warehouseHeight * gridSize);
    const fit = Math.min(usableW / baseW, usableH / baseH);
    const newZoom = +Math.max(0.1, Math.min(3, fit)).toFixed(3);

    const contentW = baseW * newZoom;
    const contentH = baseH * newZoom;
    setZoom(newZoom);
    setPan({
      x: Math.round((vw - contentW) / 2),
      y: Math.round((vh - contentH) / 2),
    });
  }, [warehouseWidth, warehouseHeight, gridSize]);

  const handleToggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      if (fullscreenRootRef.current) {
        await fullscreenRootRef.current.requestFullscreen();
      }
    } catch (err) {
      console.warn('[WMS MAP] Fullscreen toggle failed:', err);
    }
  }, []);

  // Veri ilk yüklendiğinde haritayı otomatik ekrana sığdır
  useEffect(() => {
    if (didInitialFitRef.current) return;
    if (!mapViewportRef.current) return;
    if (locations.length === 0 && entryExits.length === 0 && obstacles.length === 0) return;
    const id = window.setTimeout(() => {
      fitToScreen();
      didInitialFitRef.current = true;
    }, 50);
    return () => window.clearTimeout(id);
  }, [locations.length, entryExits.length, obstacles.length, fitToScreen]);

  // Assign colors to zones
  const zoneIds = locations.filter(l => l.type === 'Zone' && !l.isDeleted).map(l => l.id);
  const zoneColorMap = new Map<string, number>();
  zoneIds.forEach((id, i) => zoneColorMap.set(id, i));

  // --- Load data ---
  useEffect(() => {
    const load = async () => {
      try {
        const treeResp = await locationService.getTree(warehouseId);
        const data = treeResp.data?.Data || treeResp.data?.data || [];
        const items = flattenTree(data);
        setLocations(items);

        // Genişlik/Yükseklik kaynağı:
        //  1) localStorage'da kullanıcının daha önce ayarladığı boyut varsa onu al.
        //  2) Mevcut item'ların max sınırından küçükse onu yükselt (item kayıp yaşanmasın).
        //  3) Hiçbiri yoksa default 40×30.
        const stored = readStoredMapSize(warehouseId);
          let maxX = 0, maxY = 0;
          for (const item of items) {
            const right = item.gridX + item.gridWidth;
            const bottom = item.gridY + item.gridHeight;
            if (right > maxX) maxX = right;
            if (bottom > maxY) maxY = bottom;
          }
        const finalW = Math.max(stored.w ?? 40, maxX);
        const finalH = Math.max(stored.h ?? 30, maxY);
        setWarehouseWidth(finalW);
        setWarehouseHeight(finalH);
        mapSizeHydratedRef.current = true;
      } catch {
        showSnackbar(t('pages.mapEditor.loadMapError'), 'error');
      }

      // Load entry/exit points
      try {
        const eeResp = await smartWarehouseService.getEntryExits(warehouseId);
        const eeData = eeResp.data?.Data || eeResp.data?.data || [];
        setEntryExits(mapGraphNodes(eeData));
      } catch {
        // entry-exit endpoint may not exist yet, silently ignore
      }

      // Load obstacles from DB (Type='Obstacle' rows in SmartWarehouseLocations).
      // Eski sürümlerde localStorage'da kalmış engeller varsa **bir kerelik** DB'ye taşınır
      // (lazy migration) — tarayıcı bağımsız hâle gelir, çoklu cihazda görünür.
      try {
        const obsResp = await locationService.getObstacles(warehouseId);
        const obsData = obsResp.data?.Data || obsResp.data?.data || [];
        const dbObstacles: ObstacleItem[] = obsData.map((o: any) => ({
          id: o.Id || o.id,
          obstacleType: ((o.ObstacleType || o.obstacleType) as ObstacleItem['obstacleType']) || 'Wall',
          gridX: o.GridX ?? o.gridX ?? 0,
          gridY: o.GridY ?? o.gridY ?? 0,
          gridWidth: o.GridWidth ?? o.gridWidth ?? 1,
          gridHeight: o.GridHeight ?? o.gridHeight ?? 1,
          isNew: false,
          isModified: false,
          isDeleted: false,
        }));

        // Lazy migration: DB'de hiç engel yoksa ve localStorage'da varsa → DB'ye taşı
      try {
        const stored = localStorage.getItem(OBSTACLE_STORAGE_KEY + warehouseId);
          if (stored && dbObstacles.length === 0) {
            const legacy: ObstacleItem[] = JSON.parse(stored);
            const valid = (legacy || []).filter(o => o && !o.isDeleted && o.gridWidth > 0 && o.gridHeight > 0);
            if (valid.length > 0) {
              for (let i = 0; i < valid.length; i++) {
                const o = valid[i];
                try {
                  await locationService.create({
                    WarehouseId: warehouseId,
                    Code: `OBS-${(o.obstacleType || 'WALL').toUpperCase()}-${String(Date.now() + i).slice(-6)}`,
                    Name: o.obstacleType || 'Wall',
                    Type: 'Obstacle',
                    GridX: o.gridX,
                    GridY: o.gridY,
                    GridWidth: o.gridWidth,
                    GridHeight: o.gridHeight,
                  });
                } catch (mErr: any) {
                  console.warn('[WMS] Eski engel DB\'ye taşınamadı:', mErr?.response?.data || mErr.message);
                }
              }
              // Migration sonrası DB'den taze çek
              const reResp = await locationService.getObstacles(warehouseId);
              const reData = reResp.data?.Data || reResp.data?.data || [];
              dbObstacles.length = 0;
              dbObstacles.push(...reData.map((o: any) => ({
                id: o.Id || o.id,
                obstacleType: ((o.ObstacleType || o.obstacleType) as ObstacleItem['obstacleType']) || 'Wall',
                gridX: o.GridX ?? o.gridX ?? 0,
                gridY: o.GridY ?? o.gridY ?? 0,
                gridWidth: o.GridWidth ?? o.gridWidth ?? 1,
                gridHeight: o.GridHeight ?? o.gridHeight ?? 1,
                isNew: false,
                isModified: false,
                isDeleted: false,
              })));
              localStorage.removeItem(OBSTACLE_STORAGE_KEY + warehouseId);
              console.log(`[WMS] ${valid.length} eski engel localStorage'dan DB'ye taşındı.`);
            }
          }
        } catch (lsErr) {
          console.warn('[WMS] localStorage engel migration uyarısı:', lsErr);
        }

        setObstacles(dbObstacles);
      } catch (err: any) {
        console.warn('[WMS] Engeller yüklenemedi (endpoint yok mu?):', err?.response?.status);
        setObstacles([]);
      }
    };
    load();
  }, [warehouseId, showSnackbar, t]);

  // --- Undo (structural sharing) ---
  // Bu editör item'ları her zaman immutable şekilde günceller (`prev.map(l => ({ ...l, ... }))`),
  // bu yüzden undo snapshot'ı item klonlamak yerine yalnızca dizi referansını yakalar.
  // Büyük depolarda (1000+ rack) RAM tüketimi O(N×depth) → O(1×depth) düşer.
  const pushUndo = useCallback(() => {
    setUndoStack(prev => [...prev.slice(-19), {
      locations,
      entryExits,
      obstacles,
      warehouseWidth,
      warehouseHeight,
    }]);
  }, [locations, entryExits, obstacles, warehouseWidth, warehouseHeight]);

  const handleUndo = useCallback(() => {
    setUndoStack(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setLocations(last.locations);
      setEntryExits(last.entryExits);
      setObstacles(last.obstacles);
      setWarehouseWidth(last.warehouseWidth);
      setWarehouseHeight(last.warehouseHeight);
      return prev.slice(0, -1);
    });
  }, []);

  const hasChanges = locations.some(l => l.isNew || l.isModified || l.isDeleted)
    || entryExits.some(e => e.isNew || e.isModified || e.isDeleted)
    || obstacles.some(o => o.isNew || o.isModified || o.isDeleted);

  // --- Selection ---
  const getSelectedItem = useCallback((): MapSelectedItem => {
    if (!selectedId) return null;
    if (selectedKind === 'entryExit') {
      const item = entryExits.find(e => e.id === selectedId && !e.isDeleted);
      return item ? { kind: 'entryExit', item } : null;
    }
    if (selectedKind === 'obstacle') {
      const item = obstacles.find(o => o.id === selectedId && !o.isDeleted);
      return item ? { kind: 'obstacle', item } : null;
    }
    const item = locations.find(l => l.id === selectedId && !l.isDeleted);
    return item ? { kind: 'location', item } : null;
  }, [selectedId, selectedKind, locations, entryExits, obstacles]);

  const selectItem = useCallback((id: string, kind: 'location' | 'entryExit' | 'obstacle') => {
    setSelectedId(id);
    setSelectedKind(kind);
  }, []);

  // --- Faz 1: Geometri sıkılaştırma — bounds çözümleyici ---
  // Bir öğenin (location/entryExit/obstacle) hareket/boyut değişimi sırasında
  // hangi BoundsRect içinde kalması gerektiğini hesaplar.
  // - Zone/Aisle/Obstacle/EntryExit → depo bounds
  // - Aisle (parent Zone varsa) → Zone bounds
  // - Platform → koridorun yanında (X kilitli, Y koridorun yüksekliği içinde)
  // - Rack → parent Platform'un içinde
  const warehouseBounds = useMemo<BoundsRect>(
    () => ({ x: 0, y: 0, width: warehouseWidth, height: warehouseHeight }),
    [warehouseWidth, warehouseHeight],
  );

  const getBoundsForLocation = useCallback((item: LocationMapItem): BoundsRect => {
    if (item.type === 'Aisle') {
      const parent = findParentLocation(item, locations);
      if (parent && parent.type === 'Zone') return rectFromLocation(parent);
      return warehouseBounds;
    }
    if (item.type === 'Platform') {
      const aisle = item.aisleId
        ? locations.find(l => l.id === item.aisleId && !l.isDeleted)
        : null;
      if (!aisle) return warehouseBounds;

      const side = item.aisleSide;

      // Faz 4.5: peron Aisle'a TAM girmişse (INSIDE mode) bounds = aisle footprint
      // ama yürüme şeridine 1 hücre yer bırakacak şekilde kısıtlanır.
      // Resize sırasında peron kenar kolonunda/satırında kalır, diğer eksende uzayabilir.
      const inside = isPlatformInsideAisle(item, aisle);
      if (inside) {
        const ax = aisle.gridX, ay = aisle.gridY;
        const aw = aisle.gridWidth, ah = aisle.gridHeight;
        switch (side) {
          case 'Left':
            // X kilidi: aisle.gridX. Kullanıcı talebi: peron koridor boyunca tam boy uzayabilsin.
            return { x: ax, y: ay, width: Math.max(1, aw), height: ah };
          case 'Right': {
            // Peronun sağ kenarı aisle.gridX + aw'da; X = aisle.gridX + (aw - currentWidth).
            // Bounds: x kayar, x maksimum aisle.gridX + (aw - 1), genişlik aw'e kadar.
            const w = Math.min(item.gridWidth, Math.max(1, aw));
            return { x: ax + aw - w, y: ay, width: w, height: ah };
          }
          case 'Top':
            return { x: ax, y: ay, width: aw, height: Math.max(1, ah) };
          case 'Bottom': {
            const h = Math.min(item.gridHeight, Math.max(1, ah));
            return { x: ax, y: ay + ah - h, width: aw, height: h };
          }
          default:
            return rectFromLocation(aisle);
        }
      }

      // OUTSIDE mode (eski davranış):
      //   Left/Right → X koridor kenarına KILIT, Y koridor yüksekliği boyunca serbest
      //   Top/Bottom → Y koridor kenarına KILIT, X koridor genişliği boyunca serbest
      let bounds = platformBoundsForAisle(aisle, item.gridWidth, item.gridHeight, side);

      const zone = aisle.parentId
        ? locations.find(l => l.id === aisle.parentId && l.type === 'Zone' && !l.isDeleted)
        : null;
      if (zone) {
        const zb = rectFromLocation(zone);
        const x1 = Math.max(bounds.x, zb.x);
        const y1 = Math.max(bounds.y, zb.y);
        const x2 = Math.min(bounds.x + bounds.width, zb.x + zb.width);
        const y2 = Math.min(bounds.y + bounds.height, zb.y + zb.height);
        if (x2 > x1 && y2 > y1) {
          bounds = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
        }
      }
      return bounds;
    }
    if (item.type === 'Rack') {
      const parent = findParentLocation(item, locations);
      if (parent && parent.type === 'Platform') return rectFromLocation(parent);
      // Parent bağı kopuk eski veri için: mümkünse bulunduğu peronu bulup onun içine kilitle.
      const cx = item.gridX + item.gridWidth / 2;
      const cy = item.gridY + item.gridHeight / 2;
      const containingPlatform = locations.find(l =>
        l.type === 'Platform' && !l.isDeleted
        && cx >= l.gridX && cx < l.gridX + l.gridWidth
        && cy >= l.gridY && cy < l.gridY + l.gridHeight
      );
      if (containingPlatform) return rectFromLocation(containingPlatform);
      // Peron bulunamazsa rafı serbest bırakma; mevcut konumuna kilitle.
      return { x: item.gridX, y: item.gridY, width: item.gridWidth, height: item.gridHeight };
    }
    // Zone, Level, Bin → depo
    return warehouseBounds;
  }, [locations, warehouseBounds]);

  const getDescendantIds = useCallback((rootId: string): string[] => {
    const ids = new Set<string>([rootId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const l of locations) {
        if (l.isDeleted || ids.has(l.id)) continue;
        if (l.parentId && ids.has(l.parentId)) {
          ids.add(l.id);
          changed = true;
        }
      }
    }
    return Array.from(ids);
  }, [locations]);

  // Zone taşınırken beraber hareket etmesi gereken öğeleri bulur:
  // 1) İlişkisel bağlılar (parentId / aisleId)
  // 2) parent bağı kopuk olsa bile zone içinde duranlar (geometrik seed)
  const getZoneCascadeIds = useCallback((zoneId: string, zoneRect: BoundsRect): string[] => {
    const ids = new Set<string>([zoneId]);

    // Geometrik seed: merkezi zone içinde kalan location'lar da beraber taşınır.
    for (const l of locations) {
      if (l.isDeleted || l.id === zoneId || l.type === 'Zone') continue;
      const centerX = l.gridX + (l.gridWidth / 2);
      const centerY = l.gridY + (l.gridHeight / 2);
      if (isFullyInsideBounds(centerX, centerY, 0, 0, zoneRect)) {
        ids.add(l.id);
      }
    }

    let changed = true;
    while (changed) {
      changed = false;
      for (const l of locations) {
        if (l.isDeleted || ids.has(l.id)) continue;
        const parentLinked = !!l.parentId && ids.has(l.parentId);
        const aisleLinked = !!l.aisleId && ids.has(l.aisleId);
        if (parentLinked || aisleLinked) {
          ids.add(l.id);
          changed = true;
        }
      }
    }
    return Array.from(ids);
  }, [locations]);

  // Platform taşınırken child rack'leri beraber taşır.
  // parent bağı kopuk ama platform içinde duran rack'leri de kapsar.
  const getPlatformCascadeIds = useCallback((platformId: string, platformRect: BoundsRect): string[] => {
    const ids = new Set<string>(getDescendantIds(platformId));
    for (const l of locations) {
      if (l.isDeleted || l.type !== 'Rack' || l.id === platformId) continue;
      const centerX = l.gridX + (l.gridWidth / 2);
      const centerY = l.gridY + (l.gridHeight / 2);
      if (isFullyInsideBounds(centerX, centerY, 0, 0, platformRect)) {
        ids.add(l.id);
      }
    }
    return Array.from(ids);
  }, [locations, getDescendantIds]);

  // Aisle taşınırken bağlı tüm peron + raflar beraber taşınır.
  const getAisleCascadeIds = useCallback((aisleId: string): string[] => {
    const ids = new Set<string>([aisleId]);
    for (const l of locations) {
      if (l.isDeleted) continue;
      if (l.type === 'Platform' && l.aisleId === aisleId) {
        ids.add(l.id);
        for (const r of locations) {
          if (!r.isDeleted && r.type === 'Rack' && r.parentId === l.id) ids.add(r.id);
        }
      }
    }
    return Array.from(ids);
  }, [locations]);

  // Verilen rect, "yasak çakışma" yaratan bir öğeye değiyor mu?
  // Cross-type kurallar: Aisle/Platform/Rack birbirini örtmemeli; Zone yalnız Zone'la çakışmaz;
  // konteyner ilişkileri (Zone→Aisle, Platform→Rack) çakışma sayılmaz. (bkz. mapTypes.isBlockingPair)
  const collidesWithSiblings = useCallback((
    item: LocationMapItem,
    newRect: BoundsRect,
    excludeIds: Set<string>,
  ): boolean => {
    return wouldCollide(item, newRect, excludeIds, locations) !== null;
  }, [locations]);

  // Engel yerleşim kuralları (Faz 2 sıkılaştırma):
  // ✅ Aisle ile çakışabilir (engel asıl bunun için var — koridoru kesmek)
  // ✅ Zone dışında olabilir (depo içi yürünebilir boşluk)
  // ❌ Platform / Rack ile çakışamaz (peron/raf zaten yürünemez, mantıksız)
  // ❌ Diğer Obstacle ile çakışamaz (tek dikdörtgen yapsın)
  // ❌ Entry/Exit ile çakışamaz (kapı kapanmasın — Entry/Exit görseli 2x2 kaplar)
  // Dönüş: çakışan öğenin türü/etiketi (snackbar mesajı için), yoksa null
  const obstacleBlockingCollision = useCallback((
    rect: BoundsRect,
    excludeObstacleId?: string,
  ): { kind: 'Platform' | 'Rack' | 'Obstacle' | 'EntryExit'; label?: string } | null => {
    for (const l of locations) {
      if (l.isDeleted) continue;
      if (l.type !== 'Platform' && l.type !== 'Rack') continue;
      if (rectsOverlap(rect, rectFromLocation(l))) {
        return { kind: l.type, label: l.name || l.code };
      }
    }
    for (const o of obstacles) {
      if (o.isDeleted || o.id === excludeObstacleId) continue;
      const oRect: BoundsRect = { x: o.gridX, y: o.gridY, width: o.gridWidth, height: o.gridHeight };
      if (rectsOverlap(rect, oRect)) return { kind: 'Obstacle', label: o.obstacleType };
    }
    for (const ee of entryExits) {
      if (ee.isDeleted) continue;
      const eeRect: BoundsRect = { x: ee.gridX, y: ee.gridY, width: 2, height: 2 };
      if (rectsOverlap(rect, eeRect)) return { kind: 'EntryExit', label: ee.name };
    }
    return null;
  }, [locations, obstacles, entryExits]);

  // --- Bulk Rack ---
  const handleBulkRackConfirm = useCallback(() => {
    if (!bulkRackTarget || bulkRackCount < 1) return;
    pushUndo();
    const platform = bulkRackTarget;
    const count = bulkRackCount;
    const shelfLevels = Math.max(1, bulkShelfLevels);
    const cols = Math.max(1, platform.gridWidth);
    const existingCount = locations.filter(
      l => l.type === 'Rack' && l.parentId === platform.id && !l.isDeleted,
    ).length;
    const newRacks: LocationMapItem[] = [];

    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const rackIndex = existingCount + i + 1;
      const rackNum = String(rackIndex).padStart(2, '0');
      const columnId = generateTempId();
      const gridX = platform.gridX + col;
      const gridY = platform.gridY + row;

      // Haritada görünen kolon (peron pozisyonu) — toplanabilir değil, alt raflar toplanır
      newRacks.push({
        id: columnId,
        type: 'Rack',
        code: `${platform.code}-P${rackNum}`,
        name: buildDefaultLocationName('Platform', rackIndex, t),
        gridX,
        gridY,
        gridWidth: 1,
        gridHeight: 1,
        parentId: platform.id,
        aisleId: platform.aisleId,
        aisleSide: platform.aisleSide,
        sortOrder: rackIndex,
        isPickable: false,
        isNew: true,
        isModified: false,
        isDeleted: false,
      });

      // Aynı noktada üst üste dikey raf seviyeleri — sadece ağaç/detayda görünür
      for (let s = 0; s < shelfLevels; s++) {
        const shelfNum = String(s + 1).padStart(2, '0');
        newRacks.push({
          id: generateTempId(),
          type: 'Rack',
          code: `${platform.code}-P${rackNum}-R${shelfNum}`,
          name: buildDefaultLocationName('Rack', s + 1, t),
          gridX,
          gridY,
          gridWidth: 1,
          gridHeight: 1,
          parentId: columnId,
          aisleId: platform.aisleId,
          aisleSide: platform.aisleSide,
          sortOrder: s + 1,
          isPickable: true,
          isNew: true,
          isModified: false,
          isDeleted: false,
        });
      }
    }

    setLocations(prev => [...prev, ...newRacks]);
    setBulkRackTarget(null);
    setTool('select');
  }, [bulkRackTarget, bulkRackCount, bulkShelfLevels, locations, pushUndo, t]);

  // --- Map mouse handlers ---
  const handleMapMouseDown = useCallback((_e: React.MouseEvent, gridX: number, gridY: number) => {
    const gx = snapToGrid(gridX);
    const gy = snapToGrid(gridY);

    if (tool === 'zone' || tool === 'aisle' || tool === 'platform' || tool === 'obstacle') {
      // Hepsi drag-to-draw: kullanıcı dikdörtgen çizer, mouseUp'ta type'a göre işleme alınır.
      drawStart.current = { gridX: gx, gridY: gy };
      setDrawRect({ x: gx, y: gy, w: 1, h: 1 });
      return;
    }

    if (tool === 'rack') {
      const platform = locations.find(l =>
        l.type === 'Platform' && !l.isDeleted
        && gx >= l.gridX && gx < l.gridX + l.gridWidth
        && gy >= l.gridY && gy < l.gridY + l.gridHeight
      );
      if (!platform) {
        showSnackbar(t('pages.mapEditor.rackRequiresPlatform'), 'error');
        return;
      }
      // Çakışma kontrolü: aynı hücreye başka bir Rack veya yasak öğe konulmasın
      const newRect: BoundsRect = { x: gx, y: gy, width: 1, height: 1 };
      const probe: LocationMapItem = {
        id: '__probe__',
        type: 'Rack',
        code: '', name: '',
        gridX: gx, gridY: gy, gridWidth: 1, gridHeight: 1,
        parentId: platform.id, aisleId: platform.aisleId, aisleSide: platform.aisleSide,
        sortOrder: 0, isPickable: true, isNew: true, isModified: false, isDeleted: false,
      };
      const collider = wouldCollide(probe, newRect, new Set(), locations);
      if (collider) {
        const colliderName = collider.type === 'Rack' ? 'raf'
          : collider.type === 'Aisle' ? 'koridor'
          : collider.type === 'Platform' ? 'peron'
          : 'öğe';
        showSnackbar(t('pages.mapEditor.rackOverlapError', {
          defaultValue: `Bu hücre başka bir ${colliderName} tarafından kullanılıyor.`,
          colliderName,
        }), 'error');
        return;
      }
      pushUndo();
      const rackCount = locations.filter(l => l.type === 'Rack' && l.parentId === platform.id && !l.isDeleted).length;
      const rackNum = String(rackCount + 1).padStart(2, '0');
      const newRack: LocationMapItem = {
        id: generateTempId(),
        type: 'Rack',
        code: `${platform.code}-R${rackNum}`,
        name: buildDefaultLocationName('Rack', parseInt(rackNum, 10), t),
        gridX: gx,
        gridY: gy,
        gridWidth: 1,
        gridHeight: 1,
        parentId: platform.id,
        aisleId: platform.aisleId,
        aisleSide: platform.aisleSide,
        sortOrder: 0,
        isPickable: true,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setLocations(prev => [...prev, newRack]);
      selectItem(newRack.id, 'location');
      setTool('select');
      return;
    }

    if (tool === 'bulkRack') {
      const platform = locations.find(l =>
        l.type === 'Platform' && !l.isDeleted
        && gx >= l.gridX && gx < l.gridX + l.gridWidth
        && gy >= l.gridY && gy < l.gridY + l.gridHeight
      );
      if (!platform) {
        showSnackbar(t('pages.mapEditor.bulkRackRequiresPlatform'), 'error');
        return;
      }
      setBulkRackTarget(platform);
      setBulkRackCount(Math.max(1, platform.gridWidth * platform.gridHeight));
      return;
    }

    if (tool === 'entryExit') {
      pushUndo();
      const eeCount = entryExits.filter(e => !e.isDeleted).length;
      const isFirst = eeCount === 0;
      const newEE: EntryExitItem = {
        id: generateTempId(),
        nodeType: isFirst ? 'Entry' : 'Exit',
        name: isFirst ? t('pages.mapEditor.entryDefaultName') : t('pages.mapEditor.exitDefaultName', { count: eeCount }),
        gridX: gx,
        gridY: gy,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setEntryExits(prev => [...prev, newEE]);
      selectItem(newEE.id, 'entryExit');
      setTool('select');
      return;
    }

    // select tool: click empty area → deselect
    setSelectedId(null);
  }, [tool, locations, entryExits, pushUndo, showSnackbar, selectItem, t]);

  const handleMapMouseMove = useCallback((e: React.MouseEvent, gridX: number, gridY: number) => {
    // Drawing zone/aisle/platform/obstacle — depo sınırları içinde clamp (ortak preview)
    if (drawStart.current && (tool === 'zone' || tool === 'aisle' || tool === 'platform' || tool === 'obstacle')) {
      const sx = drawStart.current.gridX;
      const sy = drawStart.current.gridY;
      // Faz 1: cursor depo dışına çıksa bile preview depo içinde kalsın
      const cursorX = Math.max(0, Math.min(warehouseWidth - 1, snapToGrid(gridX)));
      const cursorY = Math.max(0, Math.min(warehouseHeight - 1, snapToGrid(gridY)));
      const x = Math.min(sx, cursorX);
      const y = Math.min(sy, cursorY);
      const w = Math.max(1, Math.abs(cursorX - sx) + 1);
      const h = Math.max(1, Math.abs(cursorY - sy) + 1);
      setDrawRect({ x, y, w, h });
      return;
    }

    // Dragging — parent-aware clamp
    if (isDragging && dragInfo.current) {
      const dxPx = e.clientX - dragInfo.current.startClientX;
      const dyPx = e.clientY - dragInfo.current.startClientY;
      const dxGrid = Math.round(dxPx / cellPx);
      const dyGrid = Math.round(dyPx / cellPx);
      // Sadece tıklama (hareketsiz mouse) durumunda hiçbir geometri güncellemesi yapma.
      // Aksi halde özellikle Platform clamp'i klikte "zıplama" etkisi yaratabiliyor.
      if (dxGrid === 0 && dyGrid === 0) return;
      const proposedX = dragInfo.current.origX + dxGrid;
      const proposedY = dragInfo.current.origY + dyGrid;
      const { id, kind } = dragInfo.current;

      if (kind === 'entryExit') {
        // Entry/Exit görsel olarak 2x2 cellPx (bkz. MapEntryExit) — bounds clamp 2x2 ile
        const { x: nx, y: ny } = clampPositionToBounds(proposedX, proposedY, 2, 2, warehouseBounds);
        setEntryExits(prev => prev.map(ee => ee.id === id
          ? { ...ee, gridX: nx, gridY: ny, isModified: !ee.isNew || ee.isModified } : ee));
      } else if (kind === 'obstacle') {
        const ob = obstacles.find(o => o.id === id);
        if (!ob) return;
        const { x: nx, y: ny } = clampPositionToBounds(proposedX, proposedY, ob.gridWidth, ob.gridHeight, warehouseBounds);
        // Çakışma kontrolü: Platform / Rack / diğer Obstacle / Entry-Exit ile çarpışırsa hareketi reddet.
        // (Aisle ile çakışma serbest — engel asıl bunun için var.)
        const proposed: BoundsRect = { x: nx, y: ny, width: ob.gridWidth, height: ob.gridHeight };
        if (obstacleBlockingCollision(proposed, ob.id)) return;
        setObstacles(prev => prev.map(o => o.id === id
          ? { ...o, gridX: nx, gridY: ny, isModified: !o.isNew || o.isModified } : o));
      } else {
        const item = locations.find(l => l.id === id);
        if (!item) return;
        // Platform sürüklemesi: kullanıcı serbestçe taşıyabilsin (depo sınırları içinde).
        // Koridor/zone snap işlemi mouseUp'ta yapılacak.
        const isPlatformDrag = item.type === 'Platform';
        const bounds = isPlatformDrag ? warehouseBounds : getBoundsForLocation(item);
        const { x: leaderX, y: leaderY } = clampPositionToBounds(
          proposedX, proposedY, item.gridWidth, item.gridHeight, bounds,
        );

        if (dragInfo.current.linkedLocationIds && dragInfo.current.linkedOriginPositions
            && dragInfo.current.linkedLocationIds.length > 1) {
          // CASCADE MOVE — Zone / Aisle / Platform
          const dx = leaderX - dragInfo.current.origX;
          const dy = leaderY - dragInfo.current.origY;
          const linkedIds = new Set(dragInfo.current.linkedLocationIds);
          const origins = dragInfo.current.linkedOriginPositions;

          // Çakışma kontrolü Platform sürüklemesinde atlanır; mouseUp snap'i çakışmayı yönetir.
          if (!isPlatformDrag) {
            const groupCollision = Array.from(linkedIds).some(linkedId => {
              const ll = locations.find(x => x.id === linkedId);
              if (!ll || ll.isDeleted) return false;
              const origin = origins[linkedId];
              if (!origin) return false;
              const newRect: BoundsRect = {
                x: origin.x + dx,
                y: origin.y + dy,
                width: ll.gridWidth,
                height: ll.gridHeight,
              };
              return collidesWithSiblings(ll, newRect, linkedIds);
            });
            if (groupCollision) return;
          }

          setLocations(prev => prev.map(l => {
            if (!linkedIds.has(l.id)) return l;
            const origin = origins[l.id];
            if (!origin) return l;
            return {
              ...l,
              gridX: origin.x + dx,
              gridY: origin.y + dy,
              isModified: !l.isNew || l.isModified,
            };
          }));
        } else {
          // SINGLE MOVE
          if (!isPlatformDrag) {
            const newRect: BoundsRect = {
              x: leaderX, y: leaderY, width: item.gridWidth, height: item.gridHeight,
            };
            if (collidesWithSiblings(item, newRect, new Set([item.id]))) return;
          }
        setLocations(prev => prev.map(l => l.id === id
            ? { ...l, gridX: leaderX, gridY: leaderY, isModified: !l.isNew || l.isModified } : l));
        }
      }
      return;
    }

    // Resizing — parent-aware clamp
    if (isResizing && resizeInfo.current) {
      const dxPx = e.clientX - resizeInfo.current.startClientX;
      const dyPx = e.clientY - resizeInfo.current.startClientY;
      const dxGrid = Math.round(dxPx / cellPx);
      const dyGrid = Math.round(dyPx / cellPx);
      const proposedW = resizeInfo.current.origW + dxGrid;
      const proposedH = resizeInfo.current.origH + dyGrid;
      const { id, kind } = resizeInfo.current;

      if (kind === 'obstacle') {
        const ob = obstacles.find(o => o.id === id);
        if (!ob) return;
        const { width, height } = clampSizeToBounds(ob.gridX, ob.gridY, proposedW, proposedH, warehouseBounds);
        // Resize sırasında Platform/Rack/diğer Obstacle/Entry-Exit ile çakışırsa boyutlandırmayı reddet.
        const proposed: BoundsRect = { x: ob.gridX, y: ob.gridY, width, height };
        if (obstacleBlockingCollision(proposed, ob.id)) return;
        setObstacles(prev => prev.map(o => o.id === id
          ? { ...o, gridWidth: width, gridHeight: height, isModified: !o.isNew || o.isModified } : o));
      } else {
        const item = locations.find(l => l.id === id);
        if (!item) return;
        let width: number;
        let height: number;
        let newX = item.gridX;
        let newY = item.gridY;

        // Faz 4.5 / 8-Light: Platform resize özel mantık.
        // Resize handle alt-sağda olduğu için Top/Left tarafında kayan-kenar gerekiyor;
        // aksi halde peron Aisle'a girer veya hiç büyüyemez.
        //
        // INSIDE peron (Aisle içinde):
        //   - Uzun eksende koridor boyu max (1 kare yürüyüş şeridi şartı eski mantığa göre kaldırıldı).
        //   - Enine eksende 1 hücre yürüyüş şeridi şartı korunur.
        //
        // OUTSIDE peron (Aisle'a yapışık dışta):
        //   - Top:    alt kenar aisle.y sabit → height arttıkça y yukarı kayar.
        //   - Left:   sağ kenar aisle.x sabit → width arttıkça x sola kayar.
        //   - Right:  sol kenar aisle.x+aw sabit → width artarken x sabit.
        //   - Bottom: üst kenar aisle.y+ah sabit → height artarken y sabit.
        //   Outer clip: peronun altında bulunduğu Zone (varsa) veya warehouse.
        if (item.type === 'Platform' && item.aisleId && item.aisleSide) {
          const aisle = locations.find(l => l.id === item.aisleId && !l.isDeleted);
          if (aisle && isPlatformInsideAisle(item, aisle)) {
            const ax = aisle.gridX;
            const ay = aisle.gridY;
            const aw = aisle.gridWidth;
            const ah = aisle.gridHeight;
            const side = item.aisleSide;

            if (side === 'Left' || side === 'Right') {
              const maxW = Math.max(1, aw);
              width = Math.min(maxW, Math.max(1, proposedW));
              height = Math.min(ah, Math.max(1, proposedH)); // boyuna: full izin
              newY = Math.min(ay + ah - height, Math.max(ay, item.gridY));
              newX = side === 'Left' ? ax : ax + aw - width;
            } else {
              const maxH = Math.max(1, ah);
              height = Math.min(maxH, Math.max(1, proposedH));
              width = Math.min(aw, Math.max(1, proposedW)); // boyuna: full izin
              newX = Math.min(ax + aw - width, Math.max(ax, item.gridX));
              newY = side === 'Top' ? ay : ay + ah - height;
            }
          } else if (aisle) {
            // OUTSIDE peron — kayan-kenar resize.
            const ax = aisle.gridX;
            const ay = aisle.gridY;
            const aw = aisle.gridWidth;
            const ah = aisle.gridHeight;
            const side = item.aisleSide;

            // Outer clip: aisle.parentId Zone ise Zone bound, değilse warehouse.
            const zone = aisle.parentId
              ? locations.find(l => l.id === aisle.parentId && l.type === 'Zone' && !l.isDeleted)
              : null;
            const outer = zone ? rectFromLocation(zone) : warehouseBounds;

            if (side === 'Left') {
              const maxW = Math.max(1, ax - outer.x);
              width = Math.min(maxW, Math.max(1, proposedW));
              newX = ax - width;
              height = Math.min(ah, Math.max(1, proposedH));
              newY = Math.min(ay + ah - height, Math.max(ay, item.gridY));
            } else if (side === 'Right') {
              const maxW = Math.max(1, outer.x + outer.width - (ax + aw));
              width = Math.min(maxW, Math.max(1, proposedW));
              newX = ax + aw;
              height = Math.min(ah, Math.max(1, proposedH));
              newY = Math.min(ay + ah - height, Math.max(ay, item.gridY));
            } else if (side === 'Top') {
              const maxH = Math.max(1, ay - outer.y);
              height = Math.min(maxH, Math.max(1, proposedH));
              newY = ay - height;
              width = Math.min(aw, Math.max(1, proposedW));
              newX = Math.min(ax + aw - width, Math.max(ax, item.gridX));
            } else { // Bottom
              const maxH = Math.max(1, outer.y + outer.height - (ay + ah));
              height = Math.min(maxH, Math.max(1, proposedH));
              newY = ay + ah;
              width = Math.min(aw, Math.max(1, proposedW));
              newX = Math.min(ax + aw - width, Math.max(ax, item.gridX));
            }
          } else {
            const bounds = getBoundsForLocation(item);
            const wh = clampSizeToBounds(item.gridX, item.gridY, proposedW, proposedH, bounds);
            width = wh.width;
            height = wh.height;
          }
        } else {
          const bounds = getBoundsForLocation(item);
          const wh = clampSizeToBounds(item.gridX, item.gridY, proposedW, proposedH, bounds);
          width = wh.width;
          height = wh.height;
        }

        setLocations(prev => prev.map(l => l.id === id
          ? { ...l,
              gridX: newX,
              gridY: newY,
              gridWidth: width,
              gridHeight: height,
              isModified: !l.isNew || l.isModified,
            }
          : l));
      }
      return;
    }
  }, [tool, isDragging, isResizing, cellPx, warehouseWidth, warehouseHeight, warehouseBounds, locations, obstacles, getBoundsForLocation, collidesWithSiblings, obstacleBlockingCollision]);

  const handleMapMouseUp = useCallback((_e: React.MouseEvent) => {
    // Finish drawing zone/aisle
    if (drawStart.current && drawRect && (tool === 'zone' || tool === 'aisle')) {
      const isZone = tool === 'zone';
      const targetType: LocationMapItem['type'] = isZone ? 'Zone' : 'Aisle';
      const count = locations.filter(l => l.type === targetType && !l.isDeleted).length;
      const colorIndex = isZone ? count : 0;

      // Faz 1: Çizim sırasında zaten clamp edildi ama emniyet için bir kez daha
      const minW = isZone ? 3 : 1;
      const minH = isZone ? 3 : 2;
      const proposedW = Math.max(minW, drawRect.w);
      const proposedH = Math.max(minH, drawRect.h);
      const { width: clampedW, height: clampedH } = clampSizeToBounds(
        drawRect.x, drawRect.y, proposedW, proposedH, warehouseBounds, minW, minH,
      );

      let aisleX = drawRect.x;
      let aisleY = drawRect.y;
      let aisleW = clampedW;
      let aisleH = clampedH;
      let parentZoneId: string | null = null;

      // Aisle ZORUNLU bir Zone içine yerleşmeli; merkezi bir Zone'a düşmüyorsa reddet,
      // düşüyorsa Zone içine clamp et (kaymadan dolayı boyut <1'e düşüyorsa reddet).
      if (!isZone) {
        const cx = drawRect.x + clampedW / 2;
        const cy = drawRect.y + clampedH / 2;
        const parentZone = findContainingZone(cx, cy, locations);
        if (!parentZone) {
          showSnackbar(t('pages.mapEditor.aisleNeedsZone', {
            defaultValue: 'Koridor bir bölgenin içinde olmalı — önce Bölge çiz.',
          }), 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }
        parentZoneId = parentZone.id;
        const zoneBounds = rectFromLocation(parentZone);
        const pos = clampPositionToBounds(aisleX, aisleY, aisleW, aisleH, zoneBounds);
        aisleX = pos.x;
        aisleY = pos.y;
        const sz = clampSizeToBounds(aisleX, aisleY, aisleW, aisleH, zoneBounds, 1, 2);
        aisleW = sz.width;
        aisleH = sz.height;
        if (aisleW < 1 || aisleH < 2) {
          showSnackbar(t('pages.mapEditor.aisleNoSpaceInZone', {
            defaultValue: 'Bu konumda koridor için bölge içinde yeterli alan yok.',
          }), 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }
      }

      // Çakışma kontrolü — cross-type:
      //   Zone     ↔ Zone (aynı tip)
      //   Aisle    ↔ Aisle | Platform | Rack (yasak)
      const newRect: BoundsRect = isZone
        ? { x: drawRect.x, y: drawRect.y, width: clampedW, height: clampedH }
        : { x: aisleX, y: aisleY, width: aisleW, height: aisleH };
      const probe: LocationMapItem = {
        id: '__probe__',
        type: targetType,
        code: '', name: '',
        gridX: newRect.x, gridY: newRect.y, gridWidth: newRect.width, gridHeight: newRect.height,
        parentId: null, aisleId: null, aisleSide: null,
        sortOrder: 0, isPickable: false, isNew: true, isModified: false, isDeleted: false,
      };
      const collider = wouldCollide(probe, newRect, new Set(), locations);
      if (collider) {
        const colliderName = collider.type === 'Zone' ? 'bölge'
          : collider.type === 'Aisle' ? 'koridor'
          : collider.type === 'Platform' ? 'peron'
          : 'raf';
        showSnackbar(t('pages.mapEditor.drawCrossOverlap', {
          defaultValue: isZone
            ? `Bölge başka bir ${colliderName}'in üstüne çizilemez.`
            : `Koridor başka bir ${colliderName}'in üstüne çizilemez.`,
          colliderName,
        }), 'error');
        drawStart.current = null;
        setDrawRect(null);
        setTool('select');
        return;
      }

      pushUndo();

      const newItem: LocationMapItem = {
        id: generateTempId(),
        type: isZone ? 'Zone' : 'Aisle',
        code: isZone ? `Z${count + 1}` : `A${count + 1}`,
        name: isZone ? buildDefaultLocationName('Zone', count + 1, t) : buildDefaultLocationName('Aisle', count + 1, t),
        gridX: newRect.x,
        gridY: newRect.y,
        gridWidth: newRect.width,
        gridHeight: newRect.height,
        parentId: parentZoneId,
        aisleId: null,
        aisleSide: null,
        sortOrder: 0,
        isPickable: false,
        isNew: true,
        isModified: false,
        isDeleted: false,
        color: isZone ? ZONE_COLORS[colorIndex % ZONE_COLORS.length] : undefined,
      };

      setLocations(prev => [...prev, newItem]);
      selectItem(newItem.id, 'location');
      drawStart.current = null;
      setDrawRect(null);
      setTool('select');
      return;
    }

    // Finish drawing platform — koridorun 4 kenarından birine yapıştır
    if (drawStart.current && drawRect && tool === 'platform') {
      const drawnRect: BoundsRect = { x: drawRect.x, y: drawRect.y, width: drawRect.w, height: drawRect.h };
      const drawCx = drawnRect.x + drawnRect.width / 2;
      const drawCy = drawnRect.y + drawnRect.height / 2;

      // 1) Önce hiç koridor var mı?
      const anyAisle = locations.some(l => !l.isDeleted && l.type === 'Aisle');
      if (!anyAisle) {
        showSnackbar(t('pages.mapEditor.needAisleFirst'), 'error');
        drawStart.current = null;
        setDrawRect(null);
        setTool('select');
        return;
      }

      // === Faz 4.5 — INSIDE mode tespiti ===
      // Çizimin BAŞLANGIÇ noktası bir koridorun içine düştüyse peron koridorun
      // footprint'i içine yerleştirilir (Koridor-Konteyner modeli).
      const startGx = drawStart.current.gridX;
      const startGy = drawStart.current.gridY;
      const containingAisle = findContainingAisle(startGx, startGy, locations);

      if (containingAisle) {
        const aisle = containingAisle;
        // Side ekseni Aisle'ın YÖNÜNE göre belirlenir:
        //   - Aisle dikey  (H > W) → Left/Right (peron sol/sağ sütuna yapışır, Y kullanıcı çizimine göre)
        //   - Aisle yatay  (W > H) → Top/Bottom (peron üst/alt satıra yapışır,  X kullanıcı çizimine göre)
        //   - Kare aisle  (W == H) → çizimin baskın eksenine göre fallback
        // Bu mantık peronu yanlış kenara "snap" etmesini engeller; kullanıcının
        // çizdiği konum mümkün olduğunca korunur.
        const aw = aisle.gridWidth;
        const ah = aisle.gridHeight;
        const aisleCx = aisle.gridX + aw / 2;
        const aisleCy = aisle.gridY + ah / 2;
        const drawCxLocal = drawnRect.x + drawnRect.width / 2;
        const drawCyLocal = drawnRect.y + drawnRect.height / 2;

        let side: AisleSide;
        if (ah > aw) {
          // Dikey aisle → çizim merkezinin X'i aisle merkezine göre Left/Right
          side = drawCxLocal <= aisleCx ? 'Left' : 'Right';
        } else if (aw > ah) {
          // Yatay aisle → çizim merkezinin Y'si aisle merkezine göre Top/Bottom
          side = drawCyLocal <= aisleCy ? 'Top' : 'Bottom';
        } else {
          // Kare aisle → çizimin baskın eksenine göre kenar
          const horizontalDominant = drawnRect.width >= drawnRect.height;
          if (horizontalDominant) {
            side = drawCyLocal <= aisleCy ? 'Top' : 'Bottom';
          } else {
            side = drawCxLocal <= aisleCx ? 'Left' : 'Right';
          }
        }

        const snappedIn = platformBoundsInsideAisle(drawnRect, aisle, side);
        if (!snappedIn) {
          showSnackbar(t('pages.mapEditor.platformNoSpace', {
            defaultValue: 'Bu koridor için yeterli iç alan yok.',
          }), 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }

        // INSIDE çakışma kontrolü:
        //  - Aisle ile çakışma istisnası wouldCollide içinde otomatik (TAM içindeyse OK).
        //  - Aynı aisle içinde başka peron varsa kenar paylaşmasın.
        const probeIn: LocationMapItem = {
          id: '__probe__',
          type: 'Platform',
          code: '', name: '',
          gridX: snappedIn.x, gridY: snappedIn.y, gridWidth: snappedIn.width, gridHeight: snappedIn.height,
          parentId: aisle.id, aisleId: aisle.id, aisleSide: side,
          sortOrder: 0, isPickable: false, isNew: true, isModified: false, isDeleted: false,
        };
        const colliderIn = wouldCollide(probeIn, snappedIn, new Set(), locations);
        if (colliderIn) {
          let msg: string;
          if (colliderIn.type === 'Platform') {
            msg = t('pages.mapEditor.platformOverlapsPlatform', {
              defaultValue: 'Bu alan başka bir peron tarafından kullanılıyor.',
            });
          } else if (colliderIn.type === 'Rack') {
            msg = t('pages.mapEditor.platformOverlapsRack', {
              defaultValue: 'Peron rafın üstüne çizilemez.',
            });
          } else {
            msg = t('pages.mapEditor.platformNoSpace', {
              defaultValue: 'Bu konumda peron için yeterli alan yok.',
            });
          }
          showSnackbar(msg, 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }

        // En az 1 hücre yürüme şeridi kalmalı
        const hypothetical = [...locations, probeIn];
        const remaining = getAisleWalkableCells(aisle, hypothetical);
        if (remaining.size < 1) {
          showSnackbar(t('pages.mapEditor.platformNoSpace', {
            defaultValue: 'Peron koridorda yürüme alanı bırakmıyor.',
          }), 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }

        pushUndo();
        // Peron numarası içinde bulunduğu koridorun numarasını temel alır.
        // Aynı koridor + aynı kenarda birden çok peron varsa "-{sıra}" eki eklenir.
        //   Koridor A2 → P2S, P2D, P2-2S ...
        const corridorNum = getAisleNumber(aisle, locations);
        const sideCode = side === 'Left' ? 'S' : side === 'Right' ? 'D' : side === 'Top' ? 'U' : 'A';
        const sameSideSeq = locations.filter(l =>
          l.type === 'Platform' && !l.isDeleted && l.aisleId === aisle.id && l.aisleSide === side,
        ).length + 1;
        const platformCode = sameSideSeq === 1
          ? `P${corridorNum}${sideCode}`
          : `P${corridorNum}-${sameSideSeq}${sideCode}`;
        const platformBaseName = buildDefaultLocationName('Platform', corridorNum, t);
        const platformName = sameSideSeq === 1 ? platformBaseName : `${platformBaseName}-${sameSideSeq}`;
        const newPlatform: LocationMapItem = {
          id: generateTempId(),
          type: 'Platform',
          code: platformCode,
          name: platformName,
          gridX: snappedIn.x,
          gridY: snappedIn.y,
          gridWidth: snappedIn.width,
          gridHeight: snappedIn.height,
          parentId: aisle.id,
          aisleId: aisle.id,
          aisleSide: side,
          sortOrder: 0,
          isPickable: false,
          isNew: true,
          isModified: false,
          isDeleted: false,
        };
        setLocations(prev => [...prev, newPlatform]);
        selectItem(newPlatform.id, 'location');
        setTool('select');
        drawStart.current = null;
        setDrawRect(null);
        return;
      }

      // === OUTSIDE mode — eski davranış (peron koridor dışına yapışır) ===
      // 2) Çizilen alana DOKUNAN/ÇAKIŞAN koridorları bul (komşuluk da dahil).
      //    Kullanıcı niyeti: peron mutlaka bir koridorun üzerinde veya kenarında çizilmiş olmalı.
      const touching = locations.filter(l => {
        if (l.isDeleted || l.type !== 'Aisle') return false;
        const ar = rectFromLocation(l);
        return !(
          drawnRect.x + drawnRect.width < ar.x
          || ar.x + ar.width < drawnRect.x
          || drawnRect.y + drawnRect.height < ar.y
          || ar.y + ar.height < drawnRect.y
        );
      });

      if (touching.length === 0) {
        showSnackbar(t('pages.mapEditor.needAisleNearby', {
          defaultValue: 'Peron eklemek için önce yakınında bir koridor olmalı.',
        }), 'error');
        drawStart.current = null;
        setDrawRect(null);
        setTool('select');
        return;
      }

      // 3) Birden fazla koridor dokunuyorsa, merkezi en yakın olanı seç
      let aisle = touching[0];
      let minDist = Infinity;
      for (const a of touching) {
        const cx = a.gridX + a.gridWidth / 2;
        const cy = a.gridY + a.gridHeight / 2;
        const d = Math.abs(drawCx - cx) + Math.abs(drawCy - cy);
        if (d < minDist) { minDist = d; aisle = a; }
      }

      // 2) Çizimin baskın eksenine göre side belirle ve snap et
      const side = determinePlatformSide(drawnRect, aisle);
      let snapped = snapPlatformToAisleSide(drawnRect, aisle, side);

      // 3) Parent zone (aisle'ın parent'ı veya geometrik kapsayan zone)
      const aisleCx = aisle.gridX + aisle.gridWidth / 2;
      const aisleCy = aisle.gridY + aisle.gridHeight / 2;
      const parentZone = (aisle.parentId
        ? locations.find(l => l.id === aisle.parentId && l.type === 'Zone' && !l.isDeleted)
        : null) || findContainingZone(aisleCx, aisleCy, locations);

      // 4) Zone içine clamp et (varsa)
      if (parentZone) {
        const zb = rectFromLocation(parentZone);
        const pos = clampPositionToBounds(snapped.x, snapped.y, snapped.width, snapped.height, zb);
        const sz = clampSizeToBounds(pos.x, pos.y, snapped.width, snapped.height, zb, 1, 1);
        snapped = { x: pos.x, y: pos.y, width: sz.width, height: sz.height };

        // Zone içine sığmıyorsa iptal
        if (!isFullyInsideBounds(snapped.x, snapped.y, snapped.width, snapped.height, zb)) {
          showSnackbar(t('pages.mapEditor.platformNoSpace', {
            defaultValue: 'Bu tarafta peron için yeterli alan yok.',
          }), 'error');
          drawStart.current = null;
          setDrawRect(null);
          setTool('select');
          return;
        }
      }

      // 5) Cross-type çakışma: koridor / başka peron / raf üstüne binmesin
      const probe: LocationMapItem = {
        id: '__probe__',
        type: 'Platform',
        code: '', name: '',
        gridX: snapped.x, gridY: snapped.y, gridWidth: snapped.width, gridHeight: snapped.height,
        parentId: aisle.id, aisleId: aisle.id, aisleSide: side,
        sortOrder: 0, isPickable: false, isNew: true, isModified: false, isDeleted: false,
      };
      const collider = wouldCollide(probe, snapped, new Set(), locations);
      if (collider) {
        let msg: string;
        if (collider.type === 'Aisle') {
          msg = t('pages.mapEditor.platformOverlapsAisle', {
            defaultValue: 'Peron koridorun üstüne çizilemez.',
          });
        } else if (collider.type === 'Platform') {
          msg = t('pages.mapEditor.platformOverlapsPlatform', {
            defaultValue: 'Bu alan başka bir peron tarafından kullanılıyor.',
          });
        } else {
          msg = t('pages.mapEditor.platformOverlapsRack', {
            defaultValue: 'Peron rafın üstüne çizilemez.',
          });
        }
        showSnackbar(msg, 'error');
        drawStart.current = null;
        setDrawRect(null);
        setTool('select');
        return;
      }

      pushUndo();
      // Peron numarası içinde bulunduğu koridorun numarasını temel alır.
      // Aynı koridor + aynı kenarda birden çok peron varsa "-{sıra}" eki eklenir.
      //   Koridor A2 → P2S, P2D, P2-2S ...
      const corridorNum = getAisleNumber(aisle, locations);
      const sideCode = side === 'Left' ? 'S' : side === 'Right' ? 'D' : side === 'Top' ? 'U' : 'A';
      const sameSideSeq = locations.filter(l =>
        l.type === 'Platform' && !l.isDeleted && l.aisleId === aisle.id && l.aisleSide === side,
      ).length + 1;
      const platformCode = sameSideSeq === 1
        ? `P${corridorNum}${sideCode}`
        : `P${corridorNum}-${sameSideSeq}${sideCode}`;
      const platformBaseName = buildDefaultLocationName('Platform', corridorNum, t);
      const platformName = sameSideSeq === 1 ? platformBaseName : `${platformBaseName}-${sameSideSeq}`;
      const newPlatform: LocationMapItem = {
        id: generateTempId(),
        type: 'Platform',
        code: platformCode,
        name: platformName,
        gridX: snapped.x,
        gridY: snapped.y,
        gridWidth: snapped.width,
        gridHeight: snapped.height,
        parentId: aisle.id,
        aisleId: aisle.id,
        aisleSide: side,
        sortOrder: 0,
        isPickable: false,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setLocations(prev => [...prev, newPlatform]);
      selectItem(newPlatform.id, 'location');
      drawStart.current = null;
      setDrawRect(null);
      setTool('select');
      return;
    }

    // Finish drawing obstacle (Faz 2 sıkılaştırma):
    //  - Depo içinde clamp (drawing sırasında zaten clamp ediliyor ama emin olalım)
    //  - Platform/Rack/diğer Obstacle/Entry-Exit ile çakışma → reddet, snackbar uyarısı
    if (drawStart.current && drawRect && tool === 'obstacle') {
      const { x: cx, y: cy, width: clampedW, height: clampedH } = (() => {
        const { x, y } = clampPositionToBounds(drawRect.x, drawRect.y, drawRect.w, drawRect.h, warehouseBounds);
        const { width, height } = clampSizeToBounds(x, y, drawRect.w, drawRect.h, warehouseBounds, 1, 1);
        return { x, y, width, height };
      })();
      const proposed: BoundsRect = { x: cx, y: cy, width: clampedW, height: clampedH };

      const collision = obstacleBlockingCollision(proposed);
      if (collision) {
        const labelByKind: Record<string, string> = {
          Platform: 'peron',
          Rack: 'raf',
          Obstacle: 'engel',
          EntryExit: 'giriş/çıkış',
        };
        const what = labelByKind[collision.kind] || 'öğe';
        showSnackbar(
          t('pages.mapEditor.obstacleOverlap', {
            defaultValue: `Bu alan başka bir ${what} tarafından kullanılıyor. Engeller koridorların üstüne konabilir ama peron/raf/giriş-çıkışın üstüne konamaz.`,
            what,
          }),
          'error',
        );
        drawStart.current = null;
        setDrawRect(null);
        setTool('select');
        return;
      }

      pushUndo();
      const newObs: ObstacleItem = {
        id: generateTempId(),
        obstacleType: 'Wall',
        gridX: cx,
        gridY: cy,
        gridWidth: clampedW,
        gridHeight: clampedH,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setObstacles(prev => [...prev, newObs]);
      selectItem(newObs.id, 'obstacle');
      drawStart.current = null;
      setDrawRect(null);
      setTool('select');
      return;
    }

    // Faz 1: Drag bitti — Aisle/Platform için parent ve side bilgilerini yeniden hesapla
    if (isDragging) {
      if (dragInfo.current && dragInfo.current.kind === 'location') {
        const id = dragInfo.current.id;
        const item = locations.find(l => l.id === id);
        if (item && item.type === 'Aisle') {
          // Aisle sürüklendikten sonra parent Zone'u yeniden tespit et
          const cx = item.gridX + item.gridWidth / 2;
          const cy = item.gridY + item.gridHeight / 2;
          const newParent = findContainingZone(cx, cy, locations);
          const newParentId = newParent?.id ?? null;
          if (newParentId !== item.parentId) {
            setLocations(prev => prev.map(l => l.id === id
              ? { ...l, parentId: newParentId, isModified: !l.isNew || l.isModified }
              : l));
          }
        } else if (item && item.type === 'Platform') {
          // Platform serbestçe sürüklendi → en yakın dokunulan koridora 4-side snap.
          // Başarısızsa peronu (ve raflarını) orijinal konumlarına geri al.
          const platformRect = rectFromLocation(item);
          const linkedSet = new Set<string>(dragInfo.current.linkedLocationIds || [item.id]);
          const origins = dragInfo.current.linkedOriginPositions || {
            [item.id]: { x: dragInfo.current.origX, y: dragInfo.current.origY },
          };

          const revert = (msg: string) => {
            setLocations(prev => prev.map(l => {
              if (!linkedSet.has(l.id)) return l;
              const o = origins[l.id];
              if (!o) return l;
              return { ...l, gridX: o.x, gridY: o.y };
            }));
            showSnackbar(msg, 'error');
          };

          const pCx = platformRect.x + platformRect.width / 2;
          const pCy = platformRect.y + platformRect.height / 2;

          // === Faz 4.5 — INSIDE mode (peron merkezi bir Aisle içine düşüyorsa) ===
          const insideAisle = findContainingAisle(pCx, pCy, locations);
          if (insideAisle) {
            const aisle = insideAisle;
            // Side: peronun merkezine en yakın aisle kenarı.
            // Aynı koridor içinde drag yapılmışsa MEVCUT side'ı korumayı dene
            // (kullanıcı boyut/konum değiştirmek isterken side flip etmesin).
            const dL = pCx - aisle.gridX;
            const dR = (aisle.gridX + aisle.gridWidth) - pCx;
            const dT = pCy - aisle.gridY;
            const dB = (aisle.gridY + aisle.gridHeight) - pCy;
            const horizontalDom = platformRect.width >= platformRect.height;
            let side: AisleSide;
            const sameAisle = item.aisleId === aisle.id;
            const prevSide = item.aisleSide;
            if (sameAisle && prevSide) {
              // Mevcut side hala geçerli mi? (örn. Left side için pCx aisle'ın sol yarısında olmalı)
              const aMidX = aisle.gridX + aisle.gridWidth / 2;
              const aMidY = aisle.gridY + aisle.gridHeight / 2;
              const stillValid =
                (prevSide === 'Left' && pCx <= aMidX) ||
                (prevSide === 'Right' && pCx >= aMidX) ||
                (prevSide === 'Top' && pCy <= aMidY) ||
                (prevSide === 'Bottom' && pCy >= aMidY);
              if (stillValid) {
                side = prevSide;
              } else if (horizontalDom && aisle.gridWidth >= 2) {
                side = dL <= dR ? 'Left' : 'Right';
              } else if (!horizontalDom && aisle.gridHeight >= 2) {
                side = dT <= dB ? 'Top' : 'Bottom';
              } else {
                const m = Math.min(dL, dR, dT, dB);
                side = m === dL ? 'Left' : m === dR ? 'Right' : m === dT ? 'Top' : 'Bottom';
              }
            } else if (horizontalDom && aisle.gridWidth >= 2) {
              side = dL <= dR ? 'Left' : 'Right';
            } else if (!horizontalDom && aisle.gridHeight >= 2) {
              side = dT <= dB ? 'Top' : 'Bottom';
            } else {
              const m = Math.min(dL, dR, dT, dB);
              side = m === dL ? 'Left' : m === dR ? 'Right' : m === dT ? 'Top' : 'Bottom';
            }

            const snappedIn = platformBoundsInsideAisle(platformRect, aisle, side);
            if (!snappedIn) {
              revert(t('pages.mapEditor.platformNoSpace', {
                defaultValue: 'Bu koridor için yeterli iç alan yok.',
              }));
              setIsDragging(false);
              dragInfo.current = null;
              return;
            }

            const probe: LocationMapItem = {
              ...item,
              gridX: snappedIn.x, gridY: snappedIn.y,
              gridWidth: snappedIn.width, gridHeight: snappedIn.height,
              parentId: aisle.id, aisleId: aisle.id, aisleSide: side,
            };
            const collIn = wouldCollide(probe, snappedIn, linkedSet, locations);
            if (collIn) {
              const msg = collIn.type === 'Platform'
                ? t('pages.mapEditor.platformOverlapsPlatform', { defaultValue: 'Bu alan başka bir peron tarafından kullanılıyor.' })
                : collIn.type === 'Rack'
                ? t('pages.mapEditor.platformOverlapsRack', { defaultValue: 'Peron rafın üstüne çizilemez.' })
                : t('pages.mapEditor.platformOverlapsAisle', { defaultValue: 'Peron koridorla taşıyor.' });
              revert(msg);
              setIsDragging(false);
              dragInfo.current = null;
              return;
            }

            const dx = snappedIn.x - item.gridX;
            const dy = snappedIn.y - item.gridY;
            setLocations(prev => prev.map(l => {
              if (l.id === item.id) {
                return {
                  ...l,
                  gridX: snappedIn.x,
                  gridY: snappedIn.y,
                  gridWidth: snappedIn.width,
                  gridHeight: snappedIn.height,
                  aisleId: aisle.id,
                  parentId: aisle.id,
                  aisleSide: side,
                  isModified: !l.isNew || l.isModified,
                };
              }
              if (linkedSet.has(l.id) && l.type === 'Rack') {
                return {
                  ...l,
                  gridX: l.gridX + dx,
                  gridY: l.gridY + dy,
                  aisleId: aisle.id,
                  aisleSide: side,
                  isModified: !l.isNew || l.isModified,
                };
              }
              return l;
            }));
            setIsDragging(false);
            dragInfo.current = null;
            return;
          }

          // === OUTSIDE mode — eski davranış ===
          const touching = locations.filter(l => {
            if (l.isDeleted || l.type !== 'Aisle') return false;
            const ar = rectFromLocation(l);
            return !(
              platformRect.x + platformRect.width < ar.x
              || ar.x + ar.width < platformRect.x
              || platformRect.y + platformRect.height < ar.y
              || ar.y + ar.height < platformRect.y
            );
          });

          if (touching.length === 0) {
            revert(t('pages.mapEditor.platformMustTouchAisle', {
              defaultValue: 'Peron bir koridora yapışmalı.',
            }));
            setIsDragging(false);
            dragInfo.current = null;
            return;
          }

          // Birden fazla koridor dokunuyorsa merkezi en yakın olanı seç
          let target = touching[0];
          let minDist = Infinity;
          for (const a of touching) {
            const d = Math.abs(pCx - (a.gridX + a.gridWidth / 2))
              + Math.abs(pCy - (a.gridY + a.gridHeight / 2));
            if (d < minDist) { minDist = d; target = a; }
          }

          // 4-side snap
          const side = determinePlatformSide(platformRect, target);
          let snapped = snapPlatformToAisleSide(platformRect, target, side);

          // Hedef koridorun parent zone'una göre clamp
          const targetZone = target.parentId
            ? locations.find(l => l.id === target.parentId && l.type === 'Zone' && !l.isDeleted)
            : null;
          if (targetZone) {
            const zb = rectFromLocation(targetZone);
            const pos = clampPositionToBounds(snapped.x, snapped.y, snapped.width, snapped.height, zb);
            snapped = { ...snapped, x: pos.x, y: pos.y };
            if (!isFullyInsideBounds(snapped.x, snapped.y, snapped.width, snapped.height, zb)) {
              revert(t('pages.mapEditor.platformNoSpace', {
                defaultValue: 'Bu tarafta peron için yeterli alan yok.',
              }));
              setIsDragging(false);
              dragInfo.current = null;
              return;
            }
          }

          // Cross-type çakışma: koridor / başka peron / başka raf üstüne binmesin
          // Kendi child rack'lerini ve item kendisini hariç tut.
          const dragCollider = wouldCollide(item, snapped, linkedSet, locations);
          if (dragCollider) {
            const msg = dragCollider.type === 'Aisle'
              ? t('pages.mapEditor.platformOverlapsAisle', { defaultValue: 'Peron koridorun üstüne çizilemez.' })
              : dragCollider.type === 'Platform'
              ? t('pages.mapEditor.platformOverlapsPlatform', { defaultValue: 'Bu alan başka bir peron tarafından kullanılıyor.' })
              : t('pages.mapEditor.platformOverlapsRack', { defaultValue: 'Peron rafın üstüne çizilemez.' });
            revert(msg);
            setIsDragging(false);
            dragInfo.current = null;
            return;
          }

          // Apply: peron + (varsa) raflar snap delta ile birlikte yerleşir
          const dx = snapped.x - item.gridX;
          const dy = snapped.y - item.gridY;
          setLocations(prev => prev.map(l => {
            if (l.id === item.id) {
              return {
                ...l,
                gridX: snapped.x,
                gridY: snapped.y,
                aisleId: target.id,
                parentId: target.id,
                aisleSide: side,
                isModified: !l.isNew || l.isModified,
              };
            }
            if (linkedSet.has(l.id) && l.type === 'Rack') {
              return {
                ...l,
                gridX: l.gridX + dx,
                gridY: l.gridY + dy,
                aisleId: target.id,
                aisleSide: side,
                isModified: !l.isNew || l.isModified,
              };
            }
            return l;
          }));
        }
      }
      setIsDragging(false);
      dragInfo.current = null;
      return;
    }
    if (isResizing) { setIsResizing(false); resizeInfo.current = null; return; }
  }, [tool, drawRect, pushUndo, locations, isDragging, isResizing, selectItem, warehouseBounds, showSnackbar, t, obstacleBlockingCollision]);

  // --- Drag/Resize start for locations ---
  const handleLocationDragStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (tool !== 'select') return;
    pushUndo();
    const item = locations.find(l => l.id === id);
    if (!item) return;
    let linkedIds: string[] | undefined;
    if (item.type === 'Zone') {
      linkedIds = getZoneCascadeIds(item.id, rectFromLocation(item));
    } else if (item.type === 'Aisle') {
      linkedIds = getAisleCascadeIds(item.id);
    } else if (item.type === 'Platform') {
      linkedIds = getPlatformCascadeIds(item.id, rectFromLocation(item));
    } else if (item.type === 'Rack') {
      linkedIds = getDescendantIds(item.id);
    }

    if (linkedIds && linkedIds.length > 1) {
      const linkedOriginPositions: Record<string, { x: number; y: number }> = {};
      const linkedSet = new Set(linkedIds);
      for (const loc of locations) {
        if (linkedSet.has(loc.id)) linkedOriginPositions[loc.id] = { x: loc.gridX, y: loc.gridY };
      }
      dragInfo.current = {
        id,
        kind: 'location',
        origX: item.gridX,
        origY: item.gridY,
        startClientX: clientX,
        startClientY: clientY,
        linkedLocationIds: linkedIds,
        linkedOriginPositions,
      };
    } else {
    dragInfo.current = { id, kind: 'location', origX: item.gridX, origY: item.gridY, startClientX: clientX, startClientY: clientY };
    }
    setIsDragging(true);
  }, [tool, pushUndo, locations, getZoneCascadeIds, getAisleCascadeIds, getPlatformCascadeIds, getDescendantIds]);

  const handleLocationResizeStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (tool !== 'select') return;
    pushUndo();
    const item = locations.find(l => l.id === id);
    if (!item) return;
    resizeInfo.current = { id, kind: 'location', origW: item.gridWidth, origH: item.gridHeight, startClientX: clientX, startClientY: clientY };
    setIsResizing(true);
  }, [tool, pushUndo, locations]);

  // --- Drag start for entry/exit ---
  const handleEEDragStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (tool !== 'select') return;
    pushUndo();
    const item = entryExits.find(e => e.id === id);
    if (!item) return;
    dragInfo.current = { id, kind: 'entryExit', origX: item.gridX, origY: item.gridY, startClientX: clientX, startClientY: clientY };
    setIsDragging(true);
  }, [tool, pushUndo, entryExits]);

  // --- Drag/Resize start for obstacles ---
  const handleObstacleDragStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (tool !== 'select') return;
    pushUndo();
    const item = obstacles.find(o => o.id === id);
    if (!item) return;
    dragInfo.current = { id, kind: 'obstacle', origX: item.gridX, origY: item.gridY, startClientX: clientX, startClientY: clientY };
    setIsDragging(true);
  }, [tool, pushUndo, obstacles]);

  const handleObstacleResizeStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (tool !== 'select') return;
    pushUndo();
    const item = obstacles.find(o => o.id === id);
    if (!item) return;
    resizeInfo.current = { id, kind: 'obstacle', origW: item.gridWidth, origH: item.gridHeight, startClientX: clientX, startClientY: clientY };
    setIsResizing(true);
  }, [tool, pushUndo, obstacles]);

  // --- Item change/delete ---
  const handleLocationChange = useCallback((id: string, changes: Partial<LocationMapItem>) => {
    setLocations(prev => prev.map(l =>
      l.id === id ? { ...l, ...changes, isModified: !l.isNew || l.isModified } : l));
  }, []);

  const handleLocationDelete = useCallback((id: string) => {
    pushUndo();
    const item = locations.find(l => l.id === id && !l.isDeleted);
    if (!item) return;

    // Cascade kümesi
    const deleteLocIds = new Set<string>([id]);
    const deleteObstacleIds = new Set<string>();
    const deleteEntryExitIds = new Set<string>();

    const itemRect = rectFromLocation(item);
    const inside = (x: number, y: number, w: number, h: number) => {
      const cx = x + w / 2;
      const cy = y + h / 2;
      return cx >= itemRect.x && cx <= itemRect.x + itemRect.width
          && cy >= itemRect.y && cy <= itemRect.y + itemRect.height;
    };

    if (item.type === 'Platform') {
      // Platform silinince child rack'leri de sil + geometric inside rack'ler
      getDescendantIds(id).forEach(x => deleteLocIds.add(x));
      for (const l of locations) {
        if (l.isDeleted || l.type !== 'Rack') continue;
        if (inside(l.gridX, l.gridY, l.gridWidth, l.gridHeight)) deleteLocIds.add(l.id);
      }
    } else if (item.type === 'Aisle') {
      // Aisle silinince:
      //  - Footprint içindeki Platform/Rack (INSIDE) silinir
      //  - parentId/aisleId bu Aisle olan Platform/Rack (OUTSIDE bağlı) silinir
      //  - Footprint içindeki Obstacle silinir
      for (const l of locations) {
        if (l.isDeleted || l.id === id) continue;
        const linked = l.parentId === id || l.aisleId === id;
        const geometric = inside(l.gridX, l.gridY, l.gridWidth, l.gridHeight);
        if (linked || geometric) deleteLocIds.add(l.id);
      }
      // descendants — silinen platformların kendi raflarına ulaş
      const seedIds = Array.from(deleteLocIds);
      for (const sid of seedIds) {
        getDescendantIds(sid).forEach(x => deleteLocIds.add(x));
      }
      for (const o of obstacles) {
        if (o.isDeleted) continue;
        if (inside(o.gridX, o.gridY, o.gridWidth, o.gridHeight)) deleteObstacleIds.add(o.id);
      }
    } else if (item.type === 'Zone') {
      // Zone silinince footprint içindeki HER ŞEY:
      // Aisle, Platform, Rack, Obstacle, EntryExit (parent bağına bakmadan).
      for (const l of locations) {
        if (l.isDeleted || l.id === id) continue;
        const linked = l.parentId === id;
        const geometric = inside(l.gridX, l.gridY, l.gridWidth, l.gridHeight);
        if (linked || geometric) deleteLocIds.add(l.id);
      }
      // descendants
      const seedIds = Array.from(deleteLocIds);
      for (const sid of seedIds) {
        getDescendantIds(sid).forEach(x => deleteLocIds.add(x));
      }
      for (const o of obstacles) {
        if (o.isDeleted) continue;
        if (inside(o.gridX, o.gridY, o.gridWidth, o.gridHeight)) deleteObstacleIds.add(o.id);
      }
      // Entry/Exit (ENTRY_EXIT_GRID_SIZE x ENTRY_EXIT_GRID_SIZE)
      for (const ee of entryExits) {
        if (ee.isDeleted) continue;
        if (inside(ee.gridX, ee.gridY, ENTRY_EXIT_GRID_SIZE, ENTRY_EXIT_GRID_SIZE)) {
          deleteEntryExitIds.add(ee.id);
        }
      }
    }

    setLocations(prev => prev.map(l =>
      deleteLocIds.has(l.id) ? { ...l, isDeleted: true } : l));
    if (deleteObstacleIds.size > 0) {
      setObstacles(prev => prev.map(o =>
        deleteObstacleIds.has(o.id) ? { ...o, isDeleted: true } : o));
    }
    if (deleteEntryExitIds.size > 0) {
      setEntryExits(prev => prev.map(ee =>
        deleteEntryExitIds.has(ee.id) ? { ...ee, isDeleted: true } : ee));
    }
    if (selectedId === id) setSelectedId(null);

    const totalSecondary = (deleteLocIds.size - 1) + deleteObstacleIds.size + deleteEntryExitIds.size;
    if (totalSecondary > 0) {
      showSnackbar(t('pages.mapEditor.cascadeDeleteInfo', {
        defaultValue: `${item.type === 'Zone' ? 'Bölge' : item.type === 'Aisle' ? 'Koridor' : 'Peron'} ile birlikte ${totalSecondary} öğe daha silindi.`,
        count: totalSecondary,
      }), 'success');
    }
  }, [pushUndo, selectedId, locations, obstacles, entryExits, getDescendantIds, showSnackbar, t]);

  const handleEntryExitChange = useCallback((id: string, changes: Partial<EntryExitItem>) => {
    setEntryExits(prev => prev.map(e =>
      e.id === id ? { ...e, ...changes, isModified: !e.isNew || e.isModified } : e));
  }, []);

  const handleEntryExitDelete = useCallback((id: string) => {
    pushUndo();
    setEntryExits(prev => prev.map(e => e.id === id ? { ...e, isDeleted: true } : e));
    if (selectedId === id) setSelectedId(null);
  }, [pushUndo, selectedId]);

  const handleObstacleChange = useCallback((id: string, changes: Partial<ObstacleItem>) => {
    // Properties paneli üzerinden manuel input geldi: konum/boyut değiştiyse cross-type
    // çakışma kontrolü yapılır (Platform/Rack/Obstacle/Entry-Exit). Çakışırsa değişiklik uygulanmaz
    // ve kullanıcıya uyarı gösterilir. obstacleType gibi non-geometric değişiklikler her zaman geçer.
    const isGeoChange = ['gridX', 'gridY', 'gridWidth', 'gridHeight'].some(k => k in changes);
    if (isGeoChange) {
      const current = obstacles.find(o => o.id === id);
      if (current) {
        const proposed: BoundsRect = {
          x: changes.gridX ?? current.gridX,
          y: changes.gridY ?? current.gridY,
          width: changes.gridWidth ?? current.gridWidth,
          height: changes.gridHeight ?? current.gridHeight,
        };
        const collision = obstacleBlockingCollision(proposed, id);
        if (collision) {
          const labelByKind: Record<string, string> = {
            Platform: 'peron', Rack: 'raf', Obstacle: 'engel', EntryExit: 'giriş/çıkış',
          };
          const what = labelByKind[collision.kind] || 'öğe';
          showSnackbar(
            t('pages.mapEditor.obstacleOverlap', {
              defaultValue: `Bu konumda engel bir ${what} ile çakışıyor.`,
              what,
            }),
            'error',
          );
          return;
        }
      }
    }
    setObstacles(prev => prev.map(o =>
      o.id === id ? { ...o, ...changes, isModified: !o.isNew || o.isModified } : o));
  }, [obstacles, obstacleBlockingCollision, showSnackbar, t]);

  const handleObstacleDelete = useCallback((id: string) => {
    pushUndo();
    setObstacles(prev => prev.map(o => o.id === id ? { ...o, isDeleted: true } : o));
    if (selectedId === id) setSelectedId(null);
  }, [pushUndo, selectedId]);

  // --- H3: Layer toggle ---
  const handleToggleLayer = useCallback((layer: keyof MapLayerVisibility) => {
    setLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  }, []);

  // --- H3: SortOrder Calculation ---
  const handleCalculateSortOrder = useCallback(async () => {
    const hasEntry = entryExits.some(e => e.nodeType === 'Entry' && !e.isDeleted);
    if (!hasEntry) {
      showSnackbar(t('pages.mapEditor.sortOrderNeedEntry'), 'error');
      return;
    }
    setSortOrderLoading(true);
    try {
      await locationService.calculateSortOrder(warehouseId);
      showSnackbar(t('pages.mapEditor.sortOrderCalculated'));
      // Reload tree to get updated sortOrder values
      const treeResp = await locationService.getTree(warehouseId);
      const data = treeResp.data?.Data || treeResp.data?.data || [];
      setLocations(flattenTree(data));
      onDataChanged();
      loadWarehouseOccupancy();
      setLayers(prev => ({ ...prev, sortOrder: true }));
    } catch (err: any) {
      const serverMsg = err?.response?.data?.Message || err?.response?.data?.message;
      showSnackbar(serverMsg || t('pages.mapEditor.sortOrderError'), 'error');
    } finally {
      setSortOrderLoading(false);
    }
  }, [warehouseId, entryExits, showSnackbar, onDataChanged, loadWarehouseOccupancy, t]);

  /**
   * Converts a list of bare pick-stop points into a proper S-shape warehouse route
   * that travels ONLY along aisle center-lines and the cross-aisle corridor.
   *
   * Grid Y: 0 = top, increases downward.
   * Aisles are vertical strips.  The cross-aisle is a horizontal corridor
   * (either above or below the aisle cluster – chosen based on entry position).
   *
   * Segments:
   *  'outbound' – entry → last pick stop  (rendered blue)
   *  'return'   – last pick stop → exit   (rendered orange)
   */
  const expandRouteThroughAisles = useCallback((basePoints: RoutePoint[]): RoutePoint[] => {
    if (basePoints.length < 2) return basePoints;

    const visLocs  = locations.filter(l => !l.isDeleted);
    const locById  = new Map(visLocs.map(l => [l.id, l] as const));
    const aisleById = new Map(
      visLocs.filter(l => l.type === 'Aisle').map(l => [l.id, l] as const),
    );

    const entryPt  = basePoints[0];
    const exitPt   = basePoints[basePoints.length - 1];
    const pickStops = basePoints.filter(p => p.locationId);

    if (pickStops.length === 0) {
      return [
        { ...entryPt, segment: 'outbound' },
        { ...exitPt,  segment: 'return'   },
      ];
    }

    // --- Helpers ---
    const result: RoutePoint[] = [];
    let lastPickResultIdx = -1;

    const add = (pt: RoutePoint) => {
      const last = result[result.length - 1];
      if (last && last.gridX === pt.gridX && last.gridY === pt.gridY) {
        // İki ayrı stop (locationId'leri farklı) aynı koridor hücresine projekte
        // ediliyorsa BİRLEŞTİRME — her seçili raf kendi pin'ini taşıyabilsin.
        // Pin koordinatları (pinGridX/pinGridY) farklı olduğu için iki ardışık
        // RoutePoint aynı çizgi koordinatında olsa bile pin'leri raf konumunda görünür.
        const sameLoc =
          !!last.locationId && !!pt.locationId && last.locationId === pt.locationId;
        const bothNonStop = !last.locationId && !pt.locationId;
        if (sameLoc || bothNonStop) {
          // Aynı duraktan iki kayıt veya etiketsiz iki ara nokta → tek tut.
          const merged: RoutePoint = { ...last };
          if (pt.label && !last.label) merged.label = pt.label;
          if (pt.locationId && !last.locationId) merged.locationId = pt.locationId;
          if (pt.pinGridX !== undefined) merged.pinGridX = pt.pinGridX;
          if (pt.pinGridY !== undefined) merged.pinGridY = pt.pinGridY;
          merged.segment = pt.segment ?? last.segment;
          merged.routeStyle = last.routeStyle ?? pt.routeStyle;
          result[result.length - 1] = merged;
          return;
        }
        // Farklı locationId iki stop ardışık aynı hücrede: ikisini de tut
        // (pin koordinatı farklı olduğu için ikisi de görünecek).
      }
      result.push(pt);
      if (pt.locationId) lastPickResultIdx = result.length - 1;
    };

    // Stop'un çizgi koordinatını koridor (aisle) merkezine projekte et.
    // Bu sayede A* rotayı koridorların içinden akıtır (rafa kadar uzanan kıvrımlar oluşmaz).
    // Pin pozisyonu (pinGridX/pinGridY) ise rafın gerçek konumunda kalır.
    const projectStopToAisle = (stop: RoutePoint): { x: number; y: number } => {
      const loc = stop.locationId ? locById.get(stop.locationId) : undefined;
      const aisle = loc?.aisleId ? aisleById.get(loc.aisleId) : undefined;
      if (!aisle) return { x: stop.gridX, y: stop.gridY };
      // Yatay koridor (genişlik > yükseklik) → Y'yi koridor merkezine sabitle, X korunur.
      // Dikey koridor → X'i koridor merkezine sabitle, Y korunur.
      const isHorizontal = aisle.gridWidth >= aisle.gridHeight;
      if (isHorizontal) {
        const cy = aisle.gridY + Math.floor(aisle.gridHeight / 2);
        const minX = aisle.gridX;
        const maxX = aisle.gridX + aisle.gridWidth - 1;
        const x = Math.max(minX, Math.min(maxX, stop.gridX));
        return { x, y: cy };
      }
      const cx = aisle.gridX + Math.floor(aisle.gridWidth / 2);
      const minY = aisle.gridY;
      const maxY = aisle.gridY + aisle.gridHeight - 1;
      const y = Math.max(minY, Math.min(maxY, stop.gridY));
      return { x: cx, y };
    };

    // --- Build route (nearest-neighbor sırası basePoints'ten geliyor) ---
    // Entry -> pick(1..N) -> Exit
    add({ ...entryPt, segment: 'outbound' });
    for (const stop of pickStops) {
      const proj = projectStopToAisle(stop);
      add({
        gridX: proj.x,
        gridY: proj.y,
        // Pin gerçek raf konumunda görünsün — birden fazla raf aynı koridor
        // hücresine düşse bile her biri kendi numarasını taşır.
        pinGridX: stop.gridX,
        pinGridY: stop.gridY,
        label: stop.label,
        locationId: stop.locationId,
        segment: 'outbound',
      });
    }
    add({ ...exitPt, segment: 'return' });

    // Retroactively mark everything after lastPickResultIdx as 'return'
    if (lastPickResultIdx >= 0) {
      for (let i = lastPickResultIdx + 1; i < result.length; i++)
        result[i] = { ...result[i], segment: 'return' };
    }

    // Faz 4: route overlay'de ardışık noktalar arası A* uygula (engel-aware çizim).
    // Not: Bu katman görsel önizleme içindir; backend strategy hesaplarını değiştirmez.
    const activeObstacles = obstacles.filter(o => !o.isDeleted);

    // Strict yürünebilir maske: Aisle hücreleri − INSIDE peronların kapladığı hücreler.
    // (Faz 4.5: peron Aisle içine TAM giriyorsa, o hücreler yürünemez kabul edilir.)
    const aisleLocs = visLocs.filter(l => l.type === 'Aisle');
    const walkableCellsByAisle = new Map<string, Set<string>>();
    const walkableAisleCells = new Set<string>();
    for (const a of aisleLocs) {
      const cells = getAisleWalkableCells(a, visLocs);
      walkableCellsByAisle.set(a.id, cells);
      cells.forEach(c => walkableAisleCells.add(c));
    }

    // Connector yürünebilir maske: Entry/Exit ↔ koridor bağlantısında
    // peronların ÜSTÜNDEN GEÇMESİN, ama koridor dışındaki boş alanı serbestçe kullansın.
    // = Tüm depo hücreleri − (Peron + Raf hücreleri).
    const connectorBlockers = new Set<string>();
    for (const l of visLocs) {
      if (l.type !== 'Platform' && l.type !== 'Rack') continue;
      for (let x = l.gridX; x < l.gridX + l.gridWidth; x++) {
        for (let y = l.gridY; y < l.gridY + l.gridHeight; y++) {
          connectorBlockers.add(`${x},${y}`);
        }
      }
    }
    const walkableConnectorCells = new Set<string>();
    for (let x = 0; x < warehouseWidth; x++) {
      for (let y = 0; y < warehouseHeight; y++) {
        const k = `${x},${y}`;
        if (!connectorBlockers.has(k)) walkableConnectorCells.add(k);
      }
    }
    const nearestWalkable = (
      x: number,
      y: number,
      preferAisleId?: string | null,
    ): { x: number; y: number } | null => {
      const pickFromSet = (cells: Set<string>) => {
        let best: { x: number; y: number } | null = null;
        let bestDist = Number.POSITIVE_INFINITY;
        cells.forEach((k) => {
          const [cx, cy] = k.split(',').map(Number);
          const d = Math.abs(cx - x) + Math.abs(cy - y);
          if (d < bestDist) {
            bestDist = d;
            best = { x: cx, y: cy };
          }
        });
        return best;
      };
      if (preferAisleId) {
        const aisleCells = walkableCellsByAisle.get(preferAisleId);
        if (aisleCells && aisleCells.size > 0) return pickFromSet(aisleCells);
      }
      return pickFromSet(walkableAisleCells);
    };
    // Entry/Exit gibi koridor-dışı noktalar için: connector maskesi üstünde BFS ile
    // ulaşılabilir en yakın koridor hücresini bul. Böylece "yakın ama kapalı" hücreye
    // bağlanmaya çalışıp dev loop üretmez.
    const nearestReachableWalkable = (
      x: number,
      y: number,
      preferAisleId?: string | null,
    ): { x: number; y: number } | null => {
      const sx = Math.round(x);
      const sy = Math.round(y);
      if (sx < 0 || sy < 0 || sx >= warehouseWidth || sy >= warehouseHeight) {
        return nearestWalkable(x, y, preferAisleId);
      }
      const key = (cx: number, cy: number) => `${cx},${cy}`;
      const queue: Array<{ x: number; y: number }> = [{ x: sx, y: sy }];
      const visited = new Set<string>([key(sx, sy)]);
      const preferSet = preferAisleId ? walkableCellsByAisle.get(preferAisleId) : null;
      let idx = 0;
      while (idx < queue.length) {
        const cur = queue[idx++];
        const k = key(cur.x, cur.y);
        const isPreferred = preferSet ? preferSet.has(k) : false;
        const isAnyAisle = walkableAisleCells.has(k);
        if ((preferSet && isPreferred) || (!preferSet && isAnyAisle)) {
          return { x: cur.x, y: cur.y };
        }
        const neighbors = [
          { x: cur.x + 1, y: cur.y },
          { x: cur.x - 1, y: cur.y },
          { x: cur.x, y: cur.y + 1 },
          { x: cur.x, y: cur.y - 1 },
        ];
        for (const nb of neighbors) {
          if (nb.x < 0 || nb.y < 0 || nb.x >= warehouseWidth || nb.y >= warehouseHeight) continue;
          const nk = key(nb.x, nb.y);
          if (visited.has(nk)) continue;
          // Başlangıç hücresini serbest bırak; devamı connector maskesinden geçmeli.
          const isStart = nb.x === sx && nb.y === sy;
          if (!isStart && !walkableConnectorCells.has(nk)) continue;
          visited.add(nk);
          queue.push(nb);
        }
      }
      return nearestWalkable(x, y, preferAisleId);
    };

    const obstacleAware: RoutePoint[] = [];
    const pushPoint = (pt: RoutePoint) => {
      const last = obstacleAware[obstacleAware.length - 1];
      if (last && last.gridX === pt.gridX && last.gridY === pt.gridY) {
        // Stop bilgisini taşıyan pt, etiketsiz bir koridor adımının üstüne geliyorsa
        // bilgileri merge et. Ancak iki ayrı locationId varsa, ikisini de saklamak için
        // pt'yi DOĞRU pinGridX/pinGridY ile ayrıca push et (pin'ler raf konumunda farklı yerde).
        const lastHasStop = !!last.locationId;
        const ptHasStop = !!pt.locationId;
        if (!lastHasStop && ptHasStop) {
          obstacleAware[obstacleAware.length - 1] = { ...last, ...pt };
          return;
        }
        if (lastHasStop && ptHasStop && last.locationId !== pt.locationId) {
          // İki farklı durak aynı koridor hücresine düşüyor → ikisini de tut.
          obstacleAware.push(pt);
          return;
        }
        if (lastHasStop && ptHasStop && last.locationId === pt.locationId) {
          // Aynı durak iki kez yazılıyor → birleştir, pinGridX/pinGridY'yi koru.
          obstacleAware[obstacleAware.length - 1] = {
            ...last,
            pinGridX: pt.pinGridX ?? last.pinGridX,
            pinGridY: pt.pinGridY ?? last.pinGridY,
            label: last.label ?? pt.label,
            segment: pt.segment ?? last.segment,
          };
          return;
        }
        if (!lastHasStop && !ptHasStop) {
          // Etiketsiz iki ara nokta → tek tut.
          obstacleAware[obstacleAware.length - 1] = { ...last, ...pt };
          return;
        }
        // last stop + pt etiketli (Giriş/Çıkış vb.) → stop bilgisini koru, segment güncelle.
        obstacleAware[obstacleAware.length - 1] = {
          ...last,
          segment: pt.segment ?? last.segment,
          routeStyle: last.routeStyle || pt.routeStyle,
        };
        return;
      }
      obstacleAware.push(pt);
    };
    // Connector sadece gerçek koridor-dışı anchor'lar için çizilsin (Giriş/Çıkış).
    // Ara/sentetik noktalar için connector çizmek, rotayı koridor dışına taşır.
    const isExternalAnchor = (pt: RoutePoint) => !!pt.label && !pt.locationId;

    for (let i = 0; i < result.length - 1; i++) {
      const from = result[i];
      const to = result[i + 1];

      if (i === 0) pushPoint(from);
      const fromAisleId = from.locationId ? locById.get(from.locationId)?.aisleId ?? null : null;
      const toAisleId = to.locationId ? locById.get(to.locationId)?.aisleId ?? null : null;
      const fromWalk = isExternalAnchor(from)
        ? nearestReachableWalkable(from.gridX, from.gridY, fromAisleId)
        : nearestWalkable(from.gridX, from.gridY, fromAisleId);
      const toWalk = isExternalAnchor(to)
        ? nearestReachableWalkable(to.gridX, to.gridY, toAisleId)
        : nearestWalkable(to.gridX, to.gridY, toAisleId);
      if (!fromWalk || !toWalk) continue;

      // Connector path (FROM koridor-dışı bir nokta → koridor walkable hücresi)
      // — peronlardan ve raflardan kaçınarak A* ile çiz.
      if (isExternalAnchor(from) && (from.gridX !== fromWalk.x || from.gridY !== fromWalk.y)) {
        const connectorPath = aStarPath(
          { gridX: from.gridX, gridY: from.gridY },
          { gridX: fromWalk.x, gridY: fromWalk.y },
          activeObstacles,
          { width: warehouseWidth, height: warehouseHeight },
          walkableConnectorCells,
        );
        for (let p = 1; p < connectorPath.length; p++) {
          pushPoint({
            gridX: connectorPath[p].gridX,
            gridY: connectorPath[p].gridY,
            segment: from.segment || to.segment,
            routeStyle: 'connector',
          });
        }
      }

      const path = aStarPath(
        { ...from, gridX: fromWalk.x, gridY: fromWalk.y },
        { ...to, gridX: toWalk.x, gridY: toWalk.y },
        activeObstacles,
        { width: warehouseWidth, height: warehouseHeight },
        walkableAisleCells,
      );

      for (let p = 1; p < path.length; p++) {
        const step = path[p];
        const isFinal = p === path.length - 1;
        if (isFinal) {
          const finalSegment = to.segment || from.segment;
          if (isExternalAnchor(to) && (to.gridX !== toWalk.x || to.gridY !== toWalk.y)) {
            // Giriş/Çıkış gibi koridor-dışı bir hedef: koridor noktasını ekle,
            // sonra connector path ile asıl anchor'a (Giriş/Çıkış pin'i) bağlan.
            pushPoint({ gridX: step.gridX, gridY: step.gridY, segment: finalSegment });
            const connectorPath = aStarPath(
              { gridX: toWalk.x, gridY: toWalk.y },
              { gridX: to.gridX, gridY: to.gridY },
              activeObstacles,
              { width: warehouseWidth, height: warehouseHeight },
              walkableConnectorCells,
            );
            for (let p2 = 1; p2 < connectorPath.length; p2++) {
              pushPoint({
                gridX: connectorPath[p2].gridX,
                gridY: connectorPath[p2].gridY,
                segment: finalSegment,
                routeStyle: 'connector',
              });
            }
            if (to.label || to.locationId) {
              pushPoint({ ...to, segment: finalSegment, routeStyle: 'connector' });
            }
          } else {
            // Pick stop'u: çizgi koridor hücresinde son bulur (step), pin
            // pinGridX/pinGridY üzerinden rafın GERÇEK konumunda gösterilir.
            // Böylece rota koridordan akar, sapma yapmaz; ama her seçili raf
            // kendi pin'ini ve durak numarasını taşır.
            pushPoint({
              ...to,
              gridX: step.gridX,
              gridY: step.gridY,
              segment: finalSegment,
            });
          }
        } else {
          pushPoint({
            gridX: step.gridX,
            gridY: step.gridY,
            segment: to.segment || from.segment,
          });
        }
      }
    }

    // Son temizlik: A-B-A türü boş U-dönüşlerini (etiketsiz orta nokta) kaldır.
    const simplified: RoutePoint[] = [];
    for (const pt of obstacleAware) {
      simplified.push(pt);
      while (simplified.length >= 3) {
        const c = simplified[simplified.length - 1];
        const b = simplified[simplified.length - 2];
        const a = simplified[simplified.length - 3];
        const isBacktrack = a.gridX === c.gridX && a.gridY === c.gridY;
        const bIsAnchor = !!b.label || !!b.locationId;
        if (isBacktrack && !bIsAnchor) {
          simplified.splice(simplified.length - 2, 1);
        } else {
          break;
        }
      }
    }

    // Durak etiketlerini fiili ziyaret sırasına göre yeniden numarala.
    // Böylece çizimde önce görülen durak "1", sonra gelen "2" olur.
    const visitOrder = new Map<string, number>();
    let seq = 1;
    for (const pt of simplified) {
      if (!pt.locationId) continue;
      if (!visitOrder.has(pt.locationId)) {
        visitOrder.set(pt.locationId, seq++);
      }
    }
    const renumbered = simplified.map((pt) => {
      if (!pt.locationId) return pt;
      const n = visitOrder.get(pt.locationId);
      if (!n) return pt;
      return { ...pt, label: String(n) };
    });

    return renumbered;
  }, [locations, obstacles, warehouseWidth, warehouseHeight]);

  // --- H3: Route Preview ---
  const buildRoutePreview = useCallback((): { previewPoints: RoutePoint[]; dist: number } | null => {
    const pickableRacks = locations.filter(l => l.type === 'Rack' && l.isPickable && !l.isDeleted && l.sortOrder > 0);
    if (ROUTE_STEP_DEBUG) {
      console.log('[WMS_ROUTE_DEBUG] buildRoutePreview:start', {
        pickableRackCount: pickableRacks.length,
      });
    }
    if (pickableRacks.length === 0) {
      showSnackbar(t('pages.mapEditor.routeNeedSortOrder'), 'error');
      return null;
    }
    if (routeRackCodes.length === 0) {
      showSnackbar('Rota için raf kodu girin. "Raflar" butonundan kodları tanımlayın.', 'error');
      return null;
    }
    // Deterministic preview: aynı veriyle her zaman aynı örnek + aynı sıra.
    // Random kaldırıldı; test karşılaştırmaları tutarlı olsun.
    const sortedByPriority = [...pickableRacks].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      if (a.gridY !== b.gridY) return a.gridY - b.gridY;
      if (a.gridX !== b.gridX) return a.gridX - b.gridX;
      return a.id.localeCompare(b.id);
    });

    const userSample = routeRackCodes
      .map(code => sortedByPriority.find(r => (r.code || '').toUpperCase() === code))
      .filter((r): r is typeof sortedByPriority[number] => !!r);

    if (userSample.length === 0) {
      showSnackbar('Girilen raf kodları haritada bulunamadı.', 'error');
      return null;
    }
    // Hibrit optimal modda sıralama bu fonksiyon içinde üretilir.
    // userSample (kullanıcının seçtiği orijinal sıra) ham olarak kullanılır.
    const sample = userSample;
    if (ROUTE_STEP_DEBUG) {
      const missingCodes = routeRackCodes.filter(code => !sample.some(r => (r.code || '').toUpperCase() === code));
      console.log('[WMS_ROUTE_DEBUG] sample-selection', {
        mode: 'user-rack-codes',
        requestedCodes: routeRackCodes,
        missingCodes,
        sample: sample.map(r => ({ id: r.id, code: r.code, sortOrder: r.sortOrder, x: r.gridX, y: r.gridY })),
      });
    }

    const entry = entryExits.find(e => e.nodeType === 'Entry' && !e.isDeleted);
    const exit = entryExits.find(e => e.nodeType === 'Exit' && !e.isDeleted);

    // ---------------------------------------------------------------
    // Yeni rota optimizasyonu: A* gerçek-mesafe matrisi + Held-Karp TSP
    // ---------------------------------------------------------------
    // Manhattan + nearest-neighbor depo rotaları için kötü bir metriktir:
    // koridor/duvarları görmez ve açgözlü olduğu için "yakın görünen ama
    // geri dönüş gerektiren" rafları seçer. Burada gerçek koridor mesafesi
    // (A*) ile mesafe matrisi kurup, n<=15 için Held-Karp DP ile garanti
    // optimal sırayı, daha büyük n için NN + 2-opt iyileştirme uygularız.
    // Çıkış sırası "S-shape / aisle-by-aisle" davranışını mesafeye yansıtarak
    // doğal olarak elde edilir; bu şekilde herhangi bir layout için iyi sonuç verir.

    const visLocsForRoute = locations.filter(l => !l.isDeleted);
    const aisleByIdForRoute = new Map(
      visLocsForRoute.filter(l => l.type === 'Aisle').map(l => [l.id, l] as const),
    );
    const stopAnchor = (loc: typeof sample[number]): { x: number; y: number } => {
      if (loc.aisleId && aisleByIdForRoute.has(loc.aisleId)) {
        const aisle = aisleByIdForRoute.get(loc.aisleId)!;
        const cx = aisle.gridX + Math.floor(aisle.gridWidth / 2);
        const cy = aisle.gridY + Math.floor(aisle.gridHeight / 2);
        const rcx = loc.gridX + Math.floor(loc.gridWidth / 2);
        const rcy = loc.gridY + Math.floor(loc.gridHeight / 2);
        if (loc.aisleSide === 'Top' || loc.aisleSide === 'Bottom') {
          return {
            x: Math.max(aisle.gridX, Math.min(aisle.gridX + aisle.gridWidth - 1, rcx)),
            y: cy,
          };
        }
        return {
          x: cx,
          y: Math.max(aisle.gridY, Math.min(aisle.gridY + aisle.gridHeight - 1, rcy)),
        };
      }
      return { x: loc.gridX, y: loc.gridY };
    };

    const aisleLocsForRoute = visLocsForRoute.filter(l => l.type === 'Aisle');
    const walkableAisleCellsForRoute = new Set<string>();
    for (const a of aisleLocsForRoute) {
      const cells = getAisleWalkableCells(a, visLocsForRoute);
      cells.forEach(c => walkableAisleCellsForRoute.add(c));
    }
    const connectorBlockersForRoute = new Set<string>();
    for (const l of visLocsForRoute) {
      if (l.type !== 'Platform' && l.type !== 'Rack') continue;
      for (let x = l.gridX; x < l.gridX + l.gridWidth; x++) {
        for (let y = l.gridY; y < l.gridY + l.gridHeight; y++) {
          connectorBlockersForRoute.add(`${x},${y}`);
        }
      }
    }
    const combinedWalkableForRoute = new Set<string>(walkableAisleCellsForRoute);
    for (let x = 0; x < warehouseWidth; x++) {
      for (let y = 0; y < warehouseHeight; y++) {
        const k = `${x},${y}`;
        if (!connectorBlockersForRoute.has(k)) combinedWalkableForRoute.add(k);
      }
    }
    const activeObstaclesForRoute = obstacles.filter(o => !o.isDeleted);

    const isInBoundsForRoute = (x: number, y: number) => (
      x >= 0 && y >= 0 && x < warehouseWidth && y < warehouseHeight
    );
    const keyForRoute = (x: number, y: number) => `${x},${y}`;
    const snapToWalkableCell = (p: { x: number; y: number }): { x: number; y: number } => {
      const sx = Math.round(p.x);
      const sy = Math.round(p.y);
      if (combinedWalkableForRoute.has(keyForRoute(sx, sy))) return { x: sx, y: sy };

      // Anchor yürünebilir hücrede değilse en yakın yürünebilir hücreye al.
      const maxR = Math.max(warehouseWidth, warehouseHeight);
      for (let r = 1; r <= maxR; r++) {
        let best: { x: number; y: number; d: number } | null = null;
        const minX = sx - r;
        const maxX = sx + r;
        const minY = sy - r;
        const maxY = sy + r;
        for (let x = minX; x <= maxX; x++) {
          for (let y = minY; y <= maxY; y++) {
            if (Math.abs(x - sx) + Math.abs(y - sy) !== r) continue;
            if (!isInBoundsForRoute(x, y)) continue;
            if (!combinedWalkableForRoute.has(keyForRoute(x, y))) continue;
            const d = Math.abs(x - sx) + Math.abs(y - sy);
            if (!best || d < best.d || (d === best.d && (y < best.y || (y === best.y && x < best.x)))) {
              best = { x, y, d };
            }
          }
        }
        if (best) return { x: best.x, y: best.y };
      }
      return { x: sx, y: sy };
    };

    // n adet pick + entry/exit (varsa). Index düzeni: 0 = entry, 1..n = picks, n+1 = exit
    const pickAnchors = sample.map((s) => snapToWalkableCell(stopAnchor(s)));
    const entryAnchor = entry
      ? snapToWalkableCell({ x: entryCenterX(entry), y: entryCenterY(entry) })
      : null;
    const exitAnchor = exit
      ? snapToWalkableCell({ x: entryCenterX(exit), y: entryCenterY(exit) })
      : null;

    const aStarCost = (
      from: { x: number; y: number },
      to: { x: number; y: number },
      mask: Set<string>,
    ): number => {
      const path = aStarPath(
        { gridX: from.x, gridY: from.y },
        { gridX: to.x, gridY: to.y },
        activeObstaclesForRoute,
        { width: warehouseWidth, height: warehouseHeight },
        mask,
      );
      if (path.length <= 1) {
        return Math.abs(from.x - to.x) + Math.abs(from.y - to.y) + 1000;
      }
      return path.length - 1;
    };

    const n = sample.length;
    const tspDist: number[][] = Array.from({ length: n + 2 }, () =>
      new Array<number>(n + 2).fill(Number.POSITIVE_INFINITY),
    );
    for (let i = 0; i < n; i++) tspDist[i + 1][i + 1] = 0;

    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) continue;
        tspDist[i + 1][j + 1] = aStarCost(pickAnchors[i], pickAnchors[j], combinedWalkableForRoute);
      }
    }
    if (entryAnchor) {
      for (let i = 0; i < n; i++) {
        tspDist[0][i + 1] = aStarCost(entryAnchor, pickAnchors[i], combinedWalkableForRoute);
      }
    }
    if (exitAnchor) {
      for (let i = 0; i < n; i++) {
        tspDist[i + 1][n + 1] = aStarCost(pickAnchors[i], exitAnchor, combinedWalkableForRoute);
      }
    }

    let orderedIdx: number[] = [];
    let optimizationMethod: 'strict-order' | 'held-karp' | 'nn-2opt' | 'hybrid-optimal' = 'held-karp';

    // Left/Right stratejilerde ilk durağı sabitle.
    // Optimal (hibrit) stratejide ilk durak sabitlenmez; gerçek en kısa tur aranır.
    let fixedFirst: number | null = null;
    let fixedSecond: number | null = null;
    if (n > 0 && routeStrategy === 'leftFirst') {
      let bestX = Number.POSITIVE_INFINITY;
      for (let i = 0; i < n; i++) {
        if (pickAnchors[i].x < bestX) { bestX = pickAnchors[i].x; fixedFirst = i; }
      }
    } else if (n > 0 && routeStrategy === 'rightFirst') {
      let bestX = Number.NEGATIVE_INFINITY;
      for (let i = 0; i < n; i++) {
        if (pickAnchors[i].x > bestX) { bestX = pickAnchors[i].x; fixedFirst = i; }
      }
    }

    if (fixedFirst !== null && n >= 2) {
      let bestIdx = -1;
      let bestCost = Number.POSITIVE_INFINITY;
      for (let i = 0; i < n; i++) {
        if (i === fixedFirst) continue;
        const raw = tspDist[fixedFirst + 1][i + 1];
        const c = Number.isFinite(raw) ? raw : 1e9;
        if (c < bestCost) {
          bestCost = c;
          bestIdx = i;
        }
      }
      fixedSecond = bestIdx >= 0 ? bestIdx : null;
    }

    const safe = (v: number) => (Number.isFinite(v) ? v : 1e9);
    const tourCost = (order: number[]) => {
      if (order.length === 0) return 0;
      let c = entryAnchor ? safe(tspDist[0][order[0] + 1]) : 0;
      for (let i = 1; i < order.length; i++) {
        c += safe(tspDist[order[i - 1] + 1][order[i] + 1]);
      }
      if (exitAnchor) {
        c += safe(tspDist[order[order.length - 1] + 1][n + 1]);
      }
      return c;
    };

    const EXACT_TSP_LIMIT = 15;
    const applyTakeAsYouGo = (order: number[]): number[] => {
      if (order.length <= 1) return [...order];

      const isPathCellTouchingStop = (stopIdx: number, px: number, py: number): boolean => {
        const anchor = pickAnchors[stopIdx];
        if (anchor.x === px && anchor.y === py) return true;

        const rack = sample[stopIdx];
        const minX = rack.gridX - 1;
        const maxX = rack.gridX + rack.gridWidth;
        const minY = rack.gridY - 1;
        const maxY = rack.gridY + rack.gridHeight;
        return px >= minX && px <= maxX && py >= minY && py <= maxY;
      };

      const pending = [...order];
      const adjusted: number[] = [];
      let current = entryAnchor
        ? { x: entryAnchor.x, y: entryAnchor.y }
        : { ...pickAnchors[pending[0]] };

      while (pending.length > 0) {
        const planned = pending[0];
        const plannedAnchor = pickAnchors[planned];

        const path = aStarPath(
          { gridX: current.x, gridY: current.y },
          { gridX: plannedAnchor.x, gridY: plannedAnchor.y },
          activeObstaclesForRoute,
          { width: warehouseWidth, height: warehouseHeight },
          combinedWalkableForRoute,
        );

        const encounteredOnPath: number[] = [];
        if (path.length > 0) {
          for (let p = 1; p < path.length; p++) {
            const cell = path[p];
            for (let pi = 0; pi < pending.length; pi++) {
              const candidateStop = pending[pi];
              if (encounteredOnPath.includes(candidateStop)) continue;
              if (isPathCellTouchingStop(candidateStop, cell.gridX, cell.gridY)) {
                encounteredOnPath.push(candidateStop);
              }
            }
          }
        }

        if (encounteredOnPath.length === 0) {
          const [taken] = pending.splice(0, 1);
          adjusted.push(taken);
          current = { ...pickAnchors[taken] };
          continue;
        }

        for (const stopIdx of encounteredOnPath) {
          const pos = pending.indexOf(stopIdx);
          if (pos < 0) continue;
          pending.splice(pos, 1);
          adjusted.push(stopIdx);
          current = { ...pickAnchors[stopIdx] };
        }
      }

      return adjusted;
    };

    const buildStrictOrderCandidate = (): number[] => {
      if (n <= 1) return Array.from({ length: n }, (_, i) => i);

      const bySortOrder = Array.from({ length: n }, (_, i) => i).sort((a, b) => {
        const sa = sample[a];
        const sb = sample[b];
        if (sa.sortOrder !== sb.sortOrder) return sa.sortOrder - sb.sortOrder;
        if (sa.gridY !== sb.gridY) return sa.gridY - sb.gridY;
        if (sa.gridX !== sb.gridX) return sa.gridX - sb.gridX;
        return sa.id.localeCompare(sb.id);
      });

      let start = 0;
      if (entryAnchor) {
        let best = Number.POSITIVE_INFINITY;
        for (let i = 0; i < bySortOrder.length; i++) {
          const idx = bySortOrder[i];
          const c = safe(tspDist[0][idx + 1]);
          if (c < best) {
            best = c;
            start = i;
          }
        }
      }

      const forward = [...bySortOrder.slice(start), ...bySortOrder.slice(0, start)];
      const backwardBase = [...bySortOrder].reverse();
      const backStart = backwardBase.indexOf(bySortOrder[start]);
      const backward = [...backwardBase.slice(backStart), ...backwardBase.slice(0, backStart)];
      let candidate = tourCost(forward) <= tourCost(backward) ? forward : backward;
      candidate = applyTakeAsYouGo(candidate);

      return candidate;
    };

    if (n === 0) {
      orderedIdx = [];
    } else if (n === 1) {
      orderedIdx = [0];
    } else if (n <= EXACT_TSP_LIMIT) {
      // Held-Karp DP TSP — entry'den başla, tüm pick'leri ziyaret et, sonunda exit (varsa).
      const FULL = (1 << n) - 1;
      const INF = Number.POSITIVE_INFINITY;
      const dp: number[][] = Array.from({ length: 1 << n }, () => new Array<number>(n).fill(INF));
      const parent: number[][] = Array.from({ length: 1 << n }, () => new Array<number>(n).fill(-1));
      if (fixedFirst !== null && fixedSecond !== null && fixedFirst !== fixedSecond) {
        const startMask = (1 << fixedFirst) | (1 << fixedSecond);
        const startCost =
          (entryAnchor ? safe(tspDist[0][fixedFirst + 1]) : 0)
          + safe(tspDist[fixedFirst + 1][fixedSecond + 1]);
        dp[startMask][fixedSecond] = startCost;
        parent[startMask][fixedSecond] = fixedFirst;
      } else {
        for (let i = 0; i < n; i++) {
          if (fixedFirst !== null && i !== fixedFirst) continue;
          dp[1 << i][i] = entryAnchor ? safe(tspDist[0][i + 1]) : 0;
        }
      }
      for (let mask = 1; mask <= FULL; mask++) {
        for (let last = 0; last < n; last++) {
          if (!(mask & (1 << last))) continue;
          const cur = dp[mask][last];
          if (!Number.isFinite(cur)) continue;
          const remaining = FULL & ~mask;
          if (remaining === 0) continue;
          for (let next = 0; next < n; next++) {
            if (!(remaining & (1 << next))) continue;
            const nMask = mask | (1 << next);
            const cand = cur + safe(tspDist[last + 1][next + 1]);
            if (cand < dp[nMask][next]) {
              dp[nMask][next] = cand;
              parent[nMask][next] = last;
            }
          }
        }
      }
      let bestEnd = -1;
      let bestCost = INF;
      for (let i = 0; i < n; i++) {
        const totalCost = dp[FULL][i] + (exitAnchor ? safe(tspDist[i + 1][n + 1]) : 0);
        if (totalCost < bestCost) {
          bestCost = totalCost;
          bestEnd = i;
        }
      }
      let mask = FULL;
      let cur = bestEnd;
      while (cur !== -1) {
        orderedIdx.unshift(cur);
        const p = parent[mask][cur];
        mask ^= 1 << cur;
        cur = p;
      }
    } else {
      // n > EXACT_TSP_LIMIT → NN + 2-opt fallback (yine A* mesafe ile).
      optimizationMethod = 'nn-2opt';
      const visited = new Array<boolean>(n).fill(false);
      let cur = -1;
      let bestStart = 0;
      const nnSteps: Array<{
        from: number;
        to: number;
        edgeCost: number;
        remaining: number;
      }> = [];
      const nnCandidateSnapshots: Array<{
        from: number;
        candidates: Array<{ to: number; cost: number }>;
      }> = [];
      const twoOptImprovements: Array<{
        i: number;
        k: number;
        oldCost: number;
        newCost: number;
      }> = [];
      const stopMeta = (idx: number) => ({
        idx,
        code: sample[idx]?.code,
        x: pickAnchors[idx]?.x,
        y: pickAnchors[idx]?.y,
      });
      if (fixedFirst !== null) {
        bestStart = fixedFirst;
      } else if (entryAnchor) {
        let bestStartCost = Number.POSITIVE_INFINITY;
        for (let i = 0; i < n; i++) {
          const c = safe(tspDist[0][i + 1]);
          if (c < bestStartCost) { bestStartCost = c; bestStart = i; }
        }
      }
      cur = bestStart;
      visited[cur] = true;
      orderedIdx.push(cur);
      if (fixedSecond !== null && fixedSecond !== bestStart) {
        orderedIdx.push(fixedSecond);
        visited[fixedSecond] = true;
        nnSteps.push({
          from: bestStart,
          to: fixedSecond,
          edgeCost: safe(tspDist[bestStart + 1][fixedSecond + 1]),
          remaining: n - 2,
        });
        cur = fixedSecond;
      }
      if (ROUTE_STEP_DEBUG) {
        console.log('[WMS_ROUTE_DEBUG] nn-2opt:start', {
          reason: `sampleSize>${EXACT_TSP_LIMIT} fallback`,
          sampleSize: n,
          threshold: EXACT_TSP_LIMIT,
          fixedFirst,
          fixedSecond,
          selectedStart: stopMeta(bestStart),
          entryAnchor,
        });
      }
      while (orderedIdx.length < n) {
        let bestNext = -1;
        let bestNextCost = Number.POSITIVE_INFINITY;
        const candidates: Array<{ to: number; cost: number }> = [];
        for (let i = 0; i < n; i++) {
          if (visited[i]) continue;
          const c = safe(tspDist[cur + 1][i + 1]);
          candidates.push({ to: i, cost: c });
          if (c < bestNextCost) { bestNextCost = c; bestNext = i; }
        }
        if (bestNext === -1) break;
        nnCandidateSnapshots.push({
          from: cur,
          candidates: candidates.sort((a, b) => a.cost - b.cost).slice(0, 5),
        });
        nnSteps.push({
          from: cur,
          to: bestNext,
          edgeCost: bestNextCost,
          remaining: n - orderedIdx.length - 1,
        });
        visited[bestNext] = true;
        orderedIdx.push(bestNext);
        cur = bestNext;
      }
      let improved = true;
      let guard = 0;
      // fixedFirst varsa 2-opt swap'i ilk index'i yer değiştirmesin diye 1'den başlat.
      const startI = fixedFirst !== null ? 1 : 0;
      while (improved && guard < 50) {
        improved = false;
        guard++;
        for (let i = startI; i < orderedIdx.length - 1; i++) {
          for (let k = i + 1; k < orderedIdx.length; k++) {
            const oldCost = tourCost(orderedIdx);
            const newOrder = [
              ...orderedIdx.slice(0, i),
              ...orderedIdx.slice(i, k + 1).reverse(),
              ...orderedIdx.slice(k + 1),
            ];
            const newCost = tourCost(newOrder);
            if (newCost + 1e-9 < oldCost) {
              twoOptImprovements.push({ i, k, oldCost, newCost });
              orderedIdx = newOrder;
              improved = true;
            }
          }
        }
      }
      if (ROUTE_STEP_DEBUG) {
        console.log('[WMS_ROUTE_DEBUG] nn-2opt:details', {
          start: stopMeta(bestStart),
          visitOrder: orderedIdx.map(stopMeta),
          nnSteps: nnSteps.map((s) => ({
            ...s,
            fromStop: stopMeta(s.from),
            toStop: stopMeta(s.to),
          })),
          nnCandidateSnapshots: nnCandidateSnapshots.map((snap) => ({
            fromStop: stopMeta(snap.from),
            topCandidates: snap.candidates.map((c) => ({
              toStop: stopMeta(c.to),
              cost: c.cost,
            })),
          })),
          twoOptImprovementCount: twoOptImprovements.length,
          twoOptImprovements,
        });
      }
    }

    // Hibrit optimal:
    // - A* tabanlı global optimizasyon sonucunu
    // - SortOrder + "geçerken al" adayına karşı kıyasla
    // ve daha kısa olanı seç.
    if (routeStrategy === 'optimal' && orderedIdx.length >= 2) {
      const strictCandidate = buildStrictOrderCandidate();
      const currentCost = tourCost(orderedIdx);
      const strictCost = tourCost(strictCandidate);
      if (strictCost + 1e-9 < currentCost) {
        orderedIdx = strictCandidate;
      }
      optimizationMethod = 'hybrid-optimal';
    }

    const beforeTakeAsYouGo = orderedIdx;
    orderedIdx = applyTakeAsYouGo(orderedIdx);
    if (ROUTE_STEP_DEBUG && orderedIdx.length === beforeTakeAsYouGo.length) {
      const changed = orderedIdx.some((idx, pos) => idx !== beforeTakeAsYouGo[pos]);
      if (changed) {
        console.log('[WMS_ROUTE_DEBUG] take-as-you-go:final-reorder', {
          before: beforeTakeAsYouGo.map((i) => ({ code: sample[i]?.code, sortOrder: sample[i]?.sortOrder })),
          after: orderedIdx.map((i) => ({ code: sample[i]?.code, sortOrder: sample[i]?.sortOrder })),
          beforeCostCells: tourCost(beforeTakeAsYouGo),
          afterCostCells: tourCost(orderedIdx),
        });
      }
    }

    // Koridor sürekliliği iyileştirmesi:
    // Bir koridordan çıkmadan önce, aynı koridorda kalan stoplar varsa onları öne al.
    // Böylece "aynı koridordan aşağı-yukarı geçip çıkıp sonra geri dönme" azaltılır.
    if (routeStrategy === 'optimal' && orderedIdx.length >= 3) {
      const aisleOf = (idx: number): string | null => sample[idx]?.aisleId || null;
      let compacted = [...orderedIdx];
      let changed = false;

      for (let p = 0; p < compacted.length - 1; p++) {
        const cur = compacted[p];
        const curAisle = aisleOf(cur);
        if (!curAisle) continue;

        const tail = compacted.slice(p + 1);
        const sameAisleTail = tail.filter((idx) => aisleOf(idx) === curAisle);
        if (sameAisleTail.length === 0) continue;

        // Zaten sıradaki stop da aynı koridorsa, koridoru henüz terk etmiyoruz.
        const next = compacted[p + 1];
        if (aisleOf(next) === curAisle) continue;

        // Koridoru terk etmeden önce aynı koridordaki kalanları "yakın olandan uzağa"
        // dizip araya al.
        const sameSorted = [...sameAisleTail].sort(
          (a, b) => safe(tspDist[cur + 1][a + 1]) - safe(tspDist[cur + 1][b + 1]),
        );
        const others = tail.filter((idx) => aisleOf(idx) !== curAisle);
        compacted = [...compacted.slice(0, p + 1), ...sameSorted, ...others];
        changed = true;
      }

      if (changed) {
        const beforeCost = tourCost(orderedIdx);
        const afterCost = tourCost(compacted);
        if (ROUTE_STEP_DEBUG) {
          console.log('[WMS_ROUTE_DEBUG] aisle-run-compaction', {
            before: orderedIdx.map((i) => ({
              idx: i,
              code: sample[i]?.code,
              aisleId: sample[i]?.aisleId || null,
            })),
            after: compacted.map((i) => ({
              idx: i,
              code: sample[i]?.code,
              aisleId: sample[i]?.aisleId || null,
            })),
            beforeCostCells: beforeCost,
            afterCostCells: afterCost,
          });
        }
        if (afterCost + 1e-9 < beforeCost) {
          orderedIdx = compacted;
        }
      }
    }

    const ordered = orderedIdx.map(i => sample[i]);
    if (ROUTE_STEP_DEBUG) {
      console.log('[WMS_ROUTE_DEBUG] tsp-optimization', {
        strategy: routeStrategy,
        fixedFirst,
        fixedSecond,
        method: optimizationMethod,
        sampleSize: n,
        finalCostCells: tourCost(orderedIdx),
        ordered: ordered.map((r, i) => ({
          step: i + 1,
          id: r.id,
          code: r.code,
          x: r.gridX,
          y: r.gridY,
          aisleId: r.aisleId,
          aisleSide: r.aisleSide,
        })),
        distMatrixCells: tspDist.map(row => row.map(v => Number.isFinite(v) ? v : null)),
      });

      const stepLines: string[] = [];
      if (orderedIdx.length > 0) {
        let running = 0;
        if (entryAnchor) {
          const first = orderedIdx[0];
          const c = safe(tspDist[0][first + 1]);
          running += c;
          stepLines.push(
            `Entry -> #1 (${sample[first]?.code || first}) cost=${c.toFixed(0)} running=${running.toFixed(0)}`
          );
        }
        for (let i = 1; i < orderedIdx.length; i++) {
          const from = orderedIdx[i - 1];
          const to = orderedIdx[i];
          const c = safe(tspDist[from + 1][to + 1]);
          running += c;
          stepLines.push(
            `#${i} (${sample[from]?.code || from}) -> #${i + 1} (${sample[to]?.code || to}) cost=${c.toFixed(0)} running=${running.toFixed(0)}`
          );
        }
        if (exitAnchor) {
          const last = orderedIdx[orderedIdx.length - 1];
          const c = safe(tspDist[last + 1][n + 1]);
          running += c;
          stepLines.push(
            `#${orderedIdx.length} (${sample[last]?.code || last}) -> Exit cost=${c.toFixed(0)} running=${running.toFixed(0)}`
          );
        }
      }
      console.groupCollapsed('[WMS_ROUTE_DEBUG] route-steps:readable');
      stepLines.forEach((line) => console.log(line));
      console.groupEnd();
    }

    const points: RoutePoint[] = [];
    if (entry) {
      points.push({ gridX: entryCenterX(entry), gridY: entryCenterY(entry), label: t('pages.mapEditor.entryLabel') });
    }
    ordered.forEach((r, i) => points.push({ gridX: r.gridX, gridY: r.gridY, label: `${i + 1}`, locationId: r.id }));
    if (exit) {
      points.push({ gridX: entryCenterX(exit), gridY: entryCenterY(exit), label: t('pages.mapEditor.exitLabel') });
    }

    const previewPoints = expandRouteThroughAisles(points);
    const dist = calculateRouteDistance(previewPoints);
    if (ROUTE_STEP_DEBUG) {
      console.log('[WMS_ROUTE_DEBUG] buildRoutePreview:result', {
        orderedStops: ordered.map((r, i) => ({
          step: i + 1,
          id: r.id,
          code: r.code,
          sortOrder: r.sortOrder,
          x: r.gridX,
          y: r.gridY,
        })),
        basePointCount: points.length,
        expandedPointCount: previewPoints.length,
        distanceMeters: dist,
      });
    }
    return { previewPoints, dist };
  }, [locations, entryExits, obstacles, warehouseWidth, warehouseHeight, routeStrategy, routeRackCodes, showSnackbar, expandRouteThroughAisles, t]);

  const handleToggleOrderRackSelection = useCallback(() => {
    setIsOrderRackSelectionActive((prev) => {
      const next = !prev;
      showSnackbar(
        next
          ? 'Sipariş raf seçimi aktif. Haritada raflara tıklayarak seçin.'
          : 'Sipariş raf seçimi kapatıldı.'
      );
      return next;
    });
  }, [showSnackbar]);

  const handleClearOrderRackSelection = useCallback(() => {
    setRouteRackCodes([]);
    setRoutePoints([]);
    setFullRoutePoints([]);
    setRouteDistance(0);
    setRouteStepIndex(-1);
    showSnackbar('Seçili sipariş rafları temizlendi.', 'success');
  }, [showSnackbar]);

  const handleRoutePreview = useCallback(() => {
    const built = buildRoutePreview();
    if (!built) return;
    const { previewPoints, dist } = built;
    setFullRoutePoints(previewPoints);
    setRoutePoints(previewPoints);
    setRouteDistance(dist);
    setRouteStepIndex(-1);
    setLayers(prev => ({ ...prev, route: true }));
  }, [buildRoutePreview]);

  // Strateji değiştirildiğinde, rota görünüyorsa otomatik yeniden üret.
  // Görünmüyorsa kullanıcı isteyince çizecek (kendiliğinden açmıyoruz).
  useEffect(() => {
    if (!layers.route) return;
    if (fullRoutePoints.length === 0) return;
    const built = buildRoutePreview();
    if (!built) return;
    setFullRoutePoints(built.previewPoints);
    setRoutePoints(built.previewPoints);
    setRouteDistance(built.dist);
    setRouteStepIndex(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStrategy]);

  const buildRouteStepTargets = useCallback((points: RoutePoint[]): RouteStepTarget[] => {
    const steps: RouteStepTarget[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < points.length; i++) {
      const pt = points[i];
      if (!pt.locationId || seen.has(pt.locationId)) continue;
      seen.add(pt.locationId);
      const num = Number(pt.label);
      const label = Number.isFinite(num) && num > 0 ? String(num) : String(steps.length + 1);
      steps.push({
        kind: 'stop',
        endIndex: i,
        label,
        gridX: pt.gridX,
        gridY: pt.gridY,
        locationId: pt.locationId,
      });
    }

    if (points.length > 0) {
      const hasReturnSegment = points.some((p) => p.segment === 'return');
      if (hasReturnSegment) {
        const lastPoint = points[points.length - 1];
        const exits = entryExits.filter((e) => e.nodeType === 'Exit' && !e.isDeleted);
        const matchedExit = exits.find((e) =>
          Math.abs(entryCenterX(e) - lastPoint.gridX) < 0.001
          && Math.abs(entryCenterY(e) - lastPoint.gridY) < 0.001
        ) || exits[0];

        steps.push({
          kind: 'exit',
          endIndex: points.length - 1,
          label: t('pages.mapEditor.exitLabel'),
          gridX: lastPoint.gridX,
          gridY: lastPoint.gridY,
          exitId: matchedExit?.id,
        });
      }
    }

    return steps;
  }, [entryExits, t]);

  const canStepRoute = useMemo(
    () => locations.some(l => l.type === 'Rack' && l.isPickable && !l.isDeleted && l.sortOrder > 0),
    [locations]
  );

  const routeStepTargets = useMemo(
    () => buildRouteStepTargets(fullRoutePoints),
    [fullRoutePoints, buildRouteStepTargets]
  );

  const handleRouteStep = useCallback(() => {
    let activeFullRoute = fullRoutePoints;
    if (activeFullRoute.length === 0) {
      const built = buildRoutePreview();
      if (!built) return;
      activeFullRoute = built.previewPoints;
      setFullRoutePoints(activeFullRoute);
    }

    const activeStepTargets = buildRouteStepTargets(activeFullRoute);
    if (activeStepTargets.length === 0) return;

    const nextIndex = routeStepIndex >= activeStepTargets.length - 1 ? 0 : routeStepIndex + 1;
    setRouteStepIndex(nextIndex);
    const nextStep = activeStepTargets[nextIndex];

    const endIdx = nextStep.endIndex;
    const steppedRoute = activeFullRoute.slice(0, endIdx + 1);
    if (ROUTE_STEP_DEBUG) {
      console.log('[WMS_ROUTE_DEBUG] route-step', {
        currentIndex: routeStepIndex,
        nextIndex,
        selectedStop: nextStep,
        selectedStopEndIndex: endIdx,
        totalStops: activeStepTargets.length,
        fullRoutePointCount: activeFullRoute.length,
        steppedRoutePointCount: steppedRoute.length,
        stops: activeStepTargets.map((s, i) => ({ step: i + 1, ...s })),
      });
    }
    setRoutePoints(steppedRoute);
    setRouteDistance(calculateRouteDistance(steppedRoute));

    if (nextStep.kind === 'stop' && nextStep.locationId) {
      setSelectedKind('location');
      setSelectedId(nextStep.locationId);
    } else if (nextStep.kind === 'exit' && nextStep.exitId) {
      setSelectedKind('entryExit');
      setSelectedId(nextStep.exitId);
    } else {
      setSelectedId(null);
    }
    setTool('select');
    setLayers(prev => ({ ...prev, route: true }));
  }, [routeStepIndex, fullRoutePoints, buildRoutePreview, buildRouteStepTargets]);

  const handleWarehouseSizeChange = useCallback((w: number, h: number) => {
    pushUndo();
    setWarehouseWidth(w);
    setWarehouseHeight(h);
  }, [pushUndo]);

  // --- Save ---
  // Yürünebilir grafı ARKA PLANDA yeniden kurar. Kaydı bloke etmez; hata/yavaşlık kaydı
  // başarısız göstermez. Aynı anda birden fazla rebuild çakışmasın diye serileştirilir
  // (uzak DB'de eşzamanlı iki tam rebuild birbirini kilitleyebiliyor).
  const rebuildInFlight = useRef(false);
  const rebuildPending = useRef(false);
  const triggerGraphRebuild = useCallback(() => {
    if (rebuildInFlight.current) {
      rebuildPending.current = true;
      return;
    }
    rebuildInFlight.current = true;
    smartWarehouseService.rebuildGraph(warehouseId)
      .catch((gErr: any) => {
        console.warn('[WMS] Graf rebuild (arka plan) başarısız:', gErr?.response?.data || gErr.message);
      })
      .finally(() => {
        rebuildInFlight.current = false;
        if (rebuildPending.current) {
          rebuildPending.current = false;
          triggerGraphRebuild();
        }
      });
  }, [warehouseId]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const newLocs = locations.filter(l => l.isNew && !l.isDeleted);
      const modifiedLocs = locations.filter(l => l.isModified && !l.isNew && !l.isDeleted);
      const deletedLocs = locations.filter(l => l.isDeleted && !l.isNew);
      const newEEs = entryExits.filter(e => e.isNew && !e.isDeleted);
      const modifiedEEs = entryExits.filter(e => e.isModified && !e.isNew && !e.isDeleted);
      const deletedEEs = entryExits.filter(e => e.isDeleted && !e.isNew);
      const newObs = obstacles.filter(o => o.isNew && !o.isDeleted);
      const modifiedObs = obstacles.filter(o => o.isModified && !o.isNew && !o.isDeleted);
      const deletedObs = obstacles.filter(o => o.isDeleted && !o.isNew);

      // === Toplu kayıt (batch) ===
      // Eski akış her yeni öğe için ayrı HTTP + ayrı DB commit yapıyordu (yüzlerce round-trip → çok yavaş)
      // ve değişen öğelerin YALNIZCA grid'ini kaydediyordu (isim/kod değişikliği kayboluyordu).
      // Artık tüm lokasyon + engel değişiklikleri tek "batch-save" isteğinde toplanıyor; graf da
      // aynı istekte yeniden kuruluyor (ayrı bir zaman aşımı riski kalmıyor).
      //
      // Not: yeni öğelerin parentId/aisleId'si temp-... olabilir; backend bunları aynı istekteki
      // TempId eşlemesiyle çözer, mevcut öğeler için ise gerçek Guid parse eder.
      const createdItems = [
        ...newLocs.map(item => ({
          TempId: item.id,
          ParentRef: item.parentId || undefined,
          AisleRef: item.aisleId || undefined,
          Code: item.code || `${item.type.charAt(0)}${Date.now().toString(36)}`,
          Name: item.name,
          Type: item.type,
          AisleSide: item.aisleSide || undefined,
          GridX: item.gridX,
          GridY: item.gridY,
          GridWidth: item.gridWidth,
          GridHeight: item.gridHeight,
        })),
        ...newObs.map((obs, idx) => ({
          TempId: obs.id,
          // idx eki: aynı istekte oluşturulan engeller aynı milisaniyede benzersiz kod alsın.
          Code: `OBS-${(obs.obstacleType || 'WALL').toUpperCase()}-${String(Date.now()).slice(-6)}${idx}`,
          Name: obs.obstacleType || 'Wall',
          Type: 'Obstacle',
          GridX: obs.gridX,
          GridY: obs.gridY,
          GridWidth: obs.gridWidth,
          GridHeight: obs.gridHeight,
        })),
      ];

      const updatedItems = [
        // Değişen lokasyonlar: isim + kod + grid (koridor ismi/kodu artık kalıcı kaydediliyor).
        ...modifiedLocs.map(item => ({
          Id: item.id,
          Code: item.code,
          Name: item.name,
          AisleSide: item.aisleSide || undefined,
          GridX: item.gridX,
          GridY: item.gridY,
          GridWidth: item.gridWidth,
          GridHeight: item.gridHeight,
        })),
        // Değişen engeller: yalnızca konum/boyut.
        ...modifiedObs.map(obs => ({
          Id: obs.id,
          GridX: obs.gridX,
          GridY: obs.gridY,
          GridWidth: obs.gridWidth,
          GridHeight: obs.gridHeight,
        })),
      ];

      const deletedIds = [
        ...deletedLocs.map(l => l.id),
        ...deletedObs.map(o => o.id),
      ];

      // Save entry/exit points (graf yeniden kurulmadan ÖNCE kaydedilmeli)
      if (newEEs.length > 0 || modifiedEEs.length > 0 || deletedEEs.length > 0) {
        for (const ee of newEEs) {
          try {
            await smartWarehouseService.createEntryExit(warehouseId, {
              NodeType: ee.nodeType,
              Name: ee.name,
              GridX: ee.gridX,
              GridY: ee.gridY,
            });
          } catch (eeErr: any) {
            console.warn('[WMS SAVE] Entry/Exit kayıt hatası:', eeErr?.response?.data);
          }
        }

        for (const ee of modifiedEEs) {
          try {
            await smartWarehouseService.updateEntryExit(warehouseId, {
              Id: ee.id,
              NodeType: ee.nodeType,
              Name: ee.name,
              GridX: ee.gridX,
              GridY: ee.gridY,
            });
          } catch (eeErr: any) {
            console.warn('[WMS SAVE] Entry/Exit güncelleme hatası:', eeErr?.response?.data);
          }
        }

        for (const ee of deletedEEs) {
          try {
            await smartWarehouseService.deleteEntryExit(warehouseId, ee.id);
          } catch (eeErr: any) {
            console.warn('[WMS SAVE] Entry/Exit silme hatası:', eeErr?.response?.data);
          }
        }
      }

      // FAZ 3: rebuild bazı eski backend sürümlerinde Entry/Exit'i silebildiği için
      // burada koruma amaçlı "eksik EE yeniden oluşturma" uygulanır (aşağıdaki guard).
      const expectedEEs = entryExits
        .filter(e => !e.isDeleted)
        .map(e => ({ nodeType: e.nodeType, name: e.name || '', gridX: e.gridX, gridY: e.gridY }));

      // Veri değişikliklerini tek istekte kaydet. ÖNEMLİ: grafı BU istekte KURMA (RebuildGraph:false).
      // Tüm depo grafını yeniden kurmak uzak DB'de yavaş olabildiğinden kaydı bloke edip 60sn
      // frontend timeout'una düşürüyordu ("Kaydetme hatası"). Kayıt hızlı dönsün; graf aşağıda
      // ARKA PLANDA tazelenir (kaydı bloke etmez, hata verse bile veri kaydı başarılıdır).
      let dataSaved = false;
      if (createdItems.length > 0 || updatedItems.length > 0 || deletedIds.length > 0) {
        await locationService.batchSave({
          WarehouseId: warehouseId,
          Created: createdItems,
          Updated: updatedItems,
          Deleted: deletedIds,
          RebuildGraph: false,
        });
        dataSaved = true;
      }

      // Rebuild sonrası EE kaybı guard'ı: eksikleri tekrar create et.
      try {
        const eeResp = await smartWarehouseService.getEntryExits(warehouseId);
        const existing = mapGraphNodes(eeResp.data?.Data || eeResp.data?.data || []);
        const hasKey = new Set(existing.map(e => `${e.nodeType}|${e.name || ''}|${e.gridX}|${e.gridY}`));
        for (const ee of expectedEEs) {
          const k = `${ee.nodeType}|${ee.name}|${ee.gridX}|${ee.gridY}`;
          if (!hasKey.has(k)) {
            await smartWarehouseService.createEntryExit(warehouseId, {
              NodeType: ee.nodeType,
              Name: ee.name,
              GridX: ee.gridX,
              GridY: ee.gridY,
            });
          }
        }
      } catch (eeGuardErr: any) {
        console.warn('[WMS SAVE] Entry/Exit guard hatası:', eeGuardErr?.response?.data || eeGuardErr.message);
      }

      showSnackbar(t('pages.mapEditor.saveSuccess'));
      onDataChanged();
      loadWarehouseOccupancy();

      // Yürünebilir grafı arka planda tazele (kaydı bloke etmez). Yalnızca yerleşim/geometri
      // veya giriş-çıkış değiştiyse gerekir; sadece isim/kod değişse bile zararsızdır.
      const layoutChanged = dataSaved
        || newEEs.length > 0 || modifiedEEs.length > 0 || deletedEEs.length > 0;
      if (layoutChanged) {
        triggerGraphRebuild();
      }

      // Reload locations
      const response = await locationService.getTree(warehouseId);
      const data = response.data?.Data || response.data?.data || [];
      setLocations(flattenTree(data));

      // Reload entry/exits
      try {
        const eeResp = await smartWarehouseService.getEntryExits(warehouseId);
        const eeData = eeResp.data?.Data || eeResp.data?.data || [];
        setEntryExits(mapGraphNodes(eeData));
      } catch {
        setEntryExits(prev => prev.filter(e => !e.isDeleted).map(e => ({ ...e, isNew: false, isModified: false })));
      }

      // Reload obstacles from DB (taze ID'ler ve durum)
      try {
        const obsResp = await locationService.getObstacles(warehouseId);
        const obsData = obsResp.data?.Data || obsResp.data?.data || [];
        const dbObstacles: ObstacleItem[] = obsData.map((o: any) => ({
          id: o.Id || o.id,
          obstacleType: ((o.ObstacleType || o.obstacleType) as ObstacleItem['obstacleType']) || 'Wall',
          gridX: o.GridX ?? o.gridX ?? 0,
          gridY: o.GridY ?? o.gridY ?? 0,
          gridWidth: o.GridWidth ?? o.gridWidth ?? 1,
          gridHeight: o.GridHeight ?? o.gridHeight ?? 1,
          isNew: false,
          isModified: false,
          isDeleted: false,
        }));
        setObstacles(dbObstacles);
      } catch {
        setObstacles(prev => prev.filter(o => !o.isDeleted).map(o => ({ ...o, isNew: false, isModified: false })));
      }

      setUndoStack([]);
    } catch (err: any) {
      console.error('[WMS] Save error response:', err?.response?.data);
      const respData = err?.response?.data;
      let msg = t('pages.mapEditor.saveError');
      if (respData?.Message || respData?.message) {
        msg = respData.Message || respData.message;
      } else if (respData?.title) {
        const errorDetails = respData.errors
          ? Object.entries(respData.errors).map(([k, v]) => `${k}: ${(v as string[]).join(', ')}`).join(' | ')
          : '';
        msg = errorDetails || respData.title;
      }
      showSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [locations, entryExits, obstacles, warehouseId, showSnackbar, onDataChanged, loadWarehouseOccupancy, t, triggerGraphRebuild]);

  // --- Duplicate selected item ---
  const handleDuplicate = useCallback(() => {
    const sel = getSelectedItem();
    if (!sel) return;
    pushUndo();

    if (sel.kind === 'location') {
      const src = sel.item;
      const newId = generateTempId();
      const sameType = locations.filter(l => l.type === src.type && !l.isDeleted);
      const nextNum = sameType.length + 1;
      const typePrefix: Record<string, string> = { Zone: 'Z', Aisle: 'A', Platform: 'P', Rack: 'R', Level: 'L', Bin: 'B' };
      const prefix = typePrefix[src.type] || src.type.charAt(0);
      const newCode = `${prefix}${nextNum}`;
      const newItem: LocationMapItem = {
        ...src,
        id: newId,
        code: newCode,
        name: `${src.name} (${t('common.copy', { defaultValue: 'copy' })})`,
        gridX: src.gridX + 1,
        gridY: src.gridY + 1,
        parentId: src.parentId,
        aisleId: src.aisleId,
        aisleSide: src.aisleSide,
        sortOrder: 0,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setLocations(prev => [...prev, newItem]);
      selectItem(newId, 'location');
    } else if (sel.kind === 'entryExit') {
      const src = sel.item;
      const newId = generateTempId();
      const newEE: EntryExitItem = {
        ...src,
        id: newId,
        name: `${src.name} (${t('common.copy', { defaultValue: 'copy' })})`,
        gridX: src.gridX + 1,
        gridY: src.gridY + 1,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setEntryExits(prev => [...prev, newEE]);
      selectItem(newId, 'entryExit');
    } else if (sel.kind === 'obstacle') {
      const src = sel.item;
      const newId = generateTempId();
      const newObs: ObstacleItem = {
        ...src,
        id: newId,
        gridX: src.gridX + 1,
        gridY: src.gridY + 1,
        isNew: true,
        isModified: false,
        isDeleted: false,
      };
      setObstacles(prev => [...prev, newObs]);
      selectItem(newId, 'obstacle');
    }
  }, [getSelectedItem, pushUndo, selectItem, locations, t]);

  // --- Render helpers ---
  const selectedItem = getSelectedItem();
  const canDuplicate = selectedItem !== null;
  const visibleLocs = useMemo(() => locations.filter(l => !l.isDeleted), [locations]);
  const visibleEEs = useMemo(() => entryExits.filter(e => !e.isDeleted), [entryExits]);
  const visibleObstacles = useMemo(() => obstacles.filter(o => !o.isDeleted), [obstacles]);

  // Büyük depolarda (binlerce raf) render performansı için raf hesaplarını TEK SEFERDE, O(n)
  // olarak hazırlıyoruz. Aksi halde her raf için isShelfLevelRack (.find) ve countShelfLevelRacks
  // (.filter) çağrılıyor, bu da O(n²) (6000 raf → ~40M işlem) yapıp her zoom/render'ı kilitliyordu.
  const rackRender = useMemo(() => {
    const byId = new Map<string, LocationMapItem>();
    const childRackCount = new Map<string, number>();
    for (const l of visibleLocs) {
      byId.set(l.id, l);
      if (l.type === 'Rack' && l.parentId) {
        childRackCount.set(l.parentId, (childRackCount.get(l.parentId) ?? 0) + 1);
      }
    }
    // Kolon raflar: shelf-level olmayanlar (parent'ıyla aynı grid konumunda olmayan raflar).
    const columnRacks = visibleLocs.filter(l => {
      if (l.type !== 'Rack') return false;
      if (!l.parentId) return true;
      const parent = byId.get(l.parentId);
      if (!parent || parent.type !== 'Rack') return true;
      const isShelf = l.gridX === parent.gridX && l.gridY === parent.gridY;
      return !isShelf;
    });
    return { childRackCount, columnRacks };
  }, [visibleLocs]);

  // Kolon rafın doluluk durumu: stok, raf seviyesindeki (üst üste dizili) alt raflara yazılır.
  // Bu alt raflar haritada çizilmediği için, doluluğu üst (kolon) rafa toplayıp renklendiriyoruz.
  //   - Hiç alt raf dolu değil  → 'empty'  (yeşil)
  //   - Bazıları dolu           → 'partial'(sarı)
  //   - Hepsi dolu              → 'full'   (turuncu)
  const rackOccupancyState = useMemo(() => {
    const occupiedChildCount = new Map<string, number>();
    for (const l of visibleLocs) {
      if (l.type !== 'Rack' || !l.parentId) continue;
      if (occupiedLocationIds.has(l.id)) {
        occupiedChildCount.set(l.parentId, (occupiedChildCount.get(l.parentId) ?? 0) + 1);
      }
    }
    const state = new Map<string, 'empty' | 'partial' | 'full'>();
    for (const col of rackRender.columnRacks) {
      const total = rackRender.childRackCount.get(col.id) ?? 0;
      const occ = occupiedChildCount.get(col.id) ?? 0;
      const selfOccupied = occupiedLocationIds.has(col.id);
      if (total > 0) {
        state.set(col.id, occ === 0 ? (selfOccupied ? 'partial' : 'empty') : occ >= total ? 'full' : 'partial');
      } else {
        state.set(col.id, selfOccupied ? 'full' : 'empty');
      }
    }
    return state;
  }, [visibleLocs, occupiedLocationIds, rackRender]);

  // Raf sürükleme handler'ı: referansı sabit kalsın ki (React.memo) zoom/pan sırasında
  // binlerce MapRack gereksiz yere yeniden render edilmesin.
  const handleRackDragStart = useCallback((id: string, clientX: number, clientY: number) => {
    if (isOrderRackSelectionActive) return;
    handleLocationDragStart(id, clientX, clientY);
  }, [isOrderRackSelectionActive, handleLocationDragStart]);

  const zoneLocs = useMemo(() => visibleLocs.filter(l => l.type === 'Zone'), [visibleLocs]);
  const aisleLocs = useMemo(() => visibleLocs.filter(l => l.type === 'Aisle'), [visibleLocs]);
  const platformLocs = useMemo(() => visibleLocs.filter(l => l.type === 'Platform'), [visibleLocs]);

  const drawBorderColor = tool === 'zone' ? ZONE_BORDER_COLORS[0]
    : tool === 'obstacle' ? '#424242' : '#757575';
  const drawBgColor = tool === 'zone' ? 'rgba(33, 150, 243, 0.10)'
    : tool === 'obstacle' ? 'rgba(66, 66, 66, 0.15)' : 'rgba(158, 158, 158, 0.15)';

  return (
    <Box ref={fullscreenRootRef} sx={{
      display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
      border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
      bgcolor: 'background.paper',
      ...(!layers.labels && { '& .map-item-label': { display: 'none' } }),
    }}>
      <MapToolbar
        tool={tool} onToolChange={setTool} zoom={zoom}
        onZoomIn={() => setZoom(z => Math.min(3, +(z + 0.15).toFixed(2)))}
        onZoomOut={() => setZoom(z => Math.max(0.1, +(z - 0.15).toFixed(2)))}
        onFitToScreen={fitToScreen}
        showGrid={showGrid} onToggleGrid={() => setShowGrid(g => !g)}
        onSave={handleSave} onUndo={handleUndo}
        saving={saving} hasChanges={hasChanges} canUndo={undoStack.length > 0}
        onCalculateSortOrder={handleCalculateSortOrder}
        sortOrderLoading={sortOrderLoading}
        onRoutePreview={handleRoutePreview}
        onToggleOrderRackSelection={handleToggleOrderRackSelection}
        orderRackSelectionActive={isOrderRackSelectionActive}
        routeRackCodesCount={routeRackCodes.length}
        onClearOrderRackSelection={handleClearOrderRackSelection}
        onRouteStep={handleRouteStep}
        canRouteStep={canStepRoute}
        routeStepText={routeStepTargets.length > 0 ? `${Math.max(routeStepIndex + 1, 0)}/${routeStepTargets.length}` : undefined}
        routeStrategy={routeStrategy}
        onRouteStrategyChange={setRouteStrategy}
        layers={layers}
        onToggleLayer={handleToggleLayer}
        onDuplicate={handleDuplicate}
        canDuplicate={canDuplicate}
        panelCollapsed={panelCollapsed}
        onTogglePanel={() => setPanelCollapsed(c => !c)}
        isFullscreen={isFullscreen}
        onToggleFullscreen={handleToggleFullscreen}
      />

      <Box ref={mapViewportRef} sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Properties panel — kapatılabilir */}
        <Box sx={{
          width: panelCollapsed ? 0 : 260,
          minWidth: panelCollapsed ? 0 : 260,
          borderRight: panelCollapsed ? 0 : '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          transition: 'width 180ms ease, min-width 180ms ease',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {!panelCollapsed && (
          <>
            <Box sx={{ overflow: 'auto' }}>
              <MapPropertiesPanel
                selected={selectedItem}
                locations={visibleLocs}
                warehouseWidth={warehouseWidth}
                warehouseHeight={warehouseHeight}
                occupancy={warehouseOccupancy}
                occupiedLocationIds={occupiedLocationIds}
                onWarehouseSizeChange={handleWarehouseSizeChange}
                onLocationChange={handleLocationChange}
                onLocationDelete={handleLocationDelete}
                onEntryExitChange={handleEntryExitChange}
                onEntryExitDelete={handleEntryExitDelete}
                onObstacleChange={handleObstacleChange}
                onObstacleDelete={handleObstacleDelete}
                onDuplicate={canDuplicate ? handleDuplicate : undefined}
              />
            </Box>
          </>
          )}
        </Box>

        {/* Map area */}
        <MapGrid
          gridSize={gridSize} zoom={zoom} pan={pan}
          warehouseWidth={warehouseWidth} warehouseHeight={warehouseHeight}
          showGrid={showGrid}
          onZoomChange={(z, newPan) => { setZoom(z); if (newPan) setPan(newPan); }}
          onPanChange={setPan}
          onMouseDown={handleMapMouseDown} onMouseMove={handleMapMouseMove} onMouseUp={handleMapMouseUp}
        >
          {/* When a drawing tool is active, disable pointer events on all items
              so clicks fall through to the grid for placement */}
          <Box sx={{ pointerEvents: tool === 'select' ? 'auto' : 'none', position: 'absolute', inset: 0 }}>
          {/* Zones (z-index 1) */}
          {zoneLocs.map(item => (
            <MapZone key={item.id} item={item} cellPx={baseCell}
              isSelected={selectedId === item.id} colorIndex={zoneColorMap.get(item.id) ?? 0}
              onSelect={id => selectItem(id, 'location')}
              onDragStart={handleLocationDragStart} onResizeStart={handleLocationResizeStart} />
          ))}

          {/* Aisles (z-index 2) */}
          {aisleLocs.map(item => (
            <MapAisle key={item.id} item={item} cellPx={baseCell}
              isSelected={selectedId === item.id}
              onSelect={id => selectItem(id, 'location')}
              onDragStart={handleLocationDragStart} onResizeStart={handleLocationResizeStart} />
          ))}

          {/* Platforms (z-index 3) */}
          {platformLocs.map(item => (
            <MapPlatform key={item.id} item={item} cellPx={baseCell}
              isSelected={selectedId === item.id}
              onSelect={id => selectItem(id, 'location')}
              onDragStart={handleLocationDragStart} onResizeStart={handleLocationResizeStart} />
          ))}

          {/* Kolon raflar haritada; altındaki dikey seviyeler ağaçta dallanır */}
          {rackRender.columnRacks.map(item => (
            <MapRack key={item.id} item={item} cellPx={baseCell}
              shelfLevelCount={rackRender.childRackCount.get(item.id) ?? 0}
              isSelected={selectedId === item.id || routeRackCodes.includes((item.code || '').toUpperCase())}
              occupancy={rackOccupancyState.get(item.id) ?? 'empty'}
              onSelect={handleRackSelect}
              onDragStart={handleRackDragStart} />
          ))}

          {/* Obstacles (z-index 15) - controlled by layer toggle */}
          {layers.obstacles && visibleObstacles.map(item => (
            <MapObstacle key={item.id} item={item} cellPx={baseCell}
              isSelected={selectedId === item.id}
              onSelect={id => selectItem(id, 'obstacle')}
              onDragStart={handleObstacleDragStart} onResizeStart={handleObstacleResizeStart} />
          ))}

          {/* Entry/Exit points (z-index 20) */}
          {visibleEEs.map(item => (
            <MapEntryExit key={item.id} item={item} cellPx={baseCell}
              isSelected={selectedId === item.id}
              onSelect={id => selectItem(id, 'entryExit')}
              onDragStart={handleEEDragStart} />
          ))}
          </Box>

          {/* H3: SortOrder overlay */}
          <MapSortOrderOverlay
            locations={visibleLocs}
            entryExits={visibleEEs}
            cellPx={baseCell}
            visible={layers.sortOrder}
          />

          {/* H3: Route preview */}
          <MapRoutePreview
            routePoints={routePoints}
            cellPx={baseCell}
            visible={layers.route}
            totalDistance={routeDistance}
            animate={routeStepIndex < 0}
          />

          {/* Drawing preview */}
          {drawRect && (
            <Box sx={{
              position: 'absolute',
              left: drawRect.x * baseCell, top: drawRect.y * baseCell,
              width: drawRect.w * baseCell, height: drawRect.h * baseCell,
              border: `2px dashed ${drawBorderColor}`,
              bgcolor: drawBgColor, borderRadius: '2px', pointerEvents: 'none', zIndex: 50,
            }} />
          )}
        </MapGrid>
      </Box>

      {/* Status bar */}
      <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, bgcolor: 'background.paper', flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusWarehouse', { w: warehouseWidth, h: warehouseHeight })}</Typography>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusZone', { count: visibleLocs.filter(l => l.type === 'Zone').length })}</Typography>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusAisle', { count: visibleLocs.filter(l => l.type === 'Aisle').length })}</Typography>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusPlatform', { count: visibleLocs.filter(l => l.type === 'Platform').length })}</Typography>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusRack', { count: visibleLocs.filter(l => l.type === 'Rack').length })}</Typography>
        <Typography variant="caption" color="text.secondary">{t('pages.mapEditor.statusEntryExit', { count: visibleEEs.length })}</Typography>
        {hasChanges && (
          <Typography variant="caption" color="warning.main" fontWeight={600}>{t('pages.mapEditor.unsavedChanges')}</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.disabled" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.7rem' }}>
          <span>🖱 {t('components.mapToolbar.zoomHint')}</span>
          <span style={{ opacity: 0.4 }}>•</span>
          <span>✋ {t('components.mapToolbar.panHint')}</span>
        </Typography>
      </Box>

      {/* Bulk Rack Dialog */}
      <Dialog open={rackInfoOpen && !!selectedRack} onClose={() => setRackInfoOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {selectedRack ? `${selectedRack.name || selectedRack.code}` : 'Raf Bilgileri'}
              </Typography>
              {selectedRack && (
                <Typography variant="caption" color="text.secondary">
                  Kod: {selectedRack.code || '-'} | SortOrder: {selectedRack.sortOrder ?? '-'}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              label={!selectedRack ? 'Raf secin' : rackStockRows.length > 0 ? 'Dolu Raf' : 'Bos Raf'}
              sx={{
                bgcolor: !selectedRack
                  ? 'grey.300'
                  : rackStockRows.length > 0
                    ? '#ef6c00'
                    : '#2e7d32',
                color: !selectedRack ? 'text.primary' : '#fff',
                fontWeight: 700,
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 260 }}>
          {!selectedRack ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Haritadan bir raf secince urunler listelenir.
            </Alert>
          ) : rackStockLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : rackStockRows.length === 0 ? (
            <Alert severity="success" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }}>
              Bu raf bos.
            </Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Urun</TableCell>
                    <TableCell>Barkod</TableCell>
                    <TableCell align="right">Miktar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rackStockRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{row.productBarcode || '-'}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRackInfoOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!bulkRackTarget} onClose={() => setBulkRackTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('pages.mapEditor.bulkRackTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {bulkRackTarget ? t('pages.mapEditor.bulkRackTarget', { name: bulkRackTarget.name }) : ''}
          </Typography>
          <TextField
            label={t('pages.mapEditor.bulkRackCount')} type="number" fullWidth size="small" autoFocus
            value={bulkRackCount}
            onChange={e => setBulkRackCount(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Dikey raf seviyesi (üst üste)"
            type="number"
            fullWidth
            size="small"
            value={bulkShelfLevels}
            onChange={e => setBulkShelfLevels(Math.max(1, Math.min(6, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 6 }}
            helperText="Her kolon için kaç raf üst üste (5-6). Ağaç görünümünde dallanır."
            sx={{ mb: 1 }}
          />
          {bulkRackTarget && (
            <Typography variant="caption" color="text.secondary">
              {t('pages.mapEditor.bulkRackArea', { w: bulkRackTarget.gridWidth, h: bulkRackTarget.gridHeight, cells: bulkRackTarget.gridWidth * bulkRackTarget.gridHeight })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRackTarget(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleBulkRackConfirm}>{t('pages.mapEditor.bulkRackCreate', { count: bulkRackCount })}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Warehouse2DMapEditor;
